/**
 * markerManager.js - Marker Tracking and UI Generation
 *
 * Manages:
 * - Marker position updates with frustum culling
 * - Occlusion testing
 * - Z-index management
 * - UI generation from markers (buildUIFromMarkers)
 */

// Auto-enable iOS debug mode to catch errors
const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
              (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
if (isIOS && !window.DEBUG_IOS_ERRORS_SET) {
    window.DEBUG_IOS_ERRORS = true;
    window.DEBUG_IOS_ERRORS_SET = true;

    // Global error handler to catch uncaught errors before page reload
    window.onerror = function(message, source, lineno, colno, error) {
        var stack = 'N/A';
        if (error && error.stack) stack = error.stack.substring(0, 400);
        console.error('[GLOBAL ERROR]', { message, source, lineno, colno, stack });
        return true; // Prevent default handling
    };

    window.onunhandledrejection = function(event) {
        var reason = event.reason;
        var msg = reason ? (reason.message || reason) : 'Unknown';
        var stack = (reason && reason.stack) ? reason.stack.substring(0, 400) : 'N/A';
        console.error('[UNHANDLED PROMISE]', { message: msg, stack });
    };

    // WebGL context lost handler - iOS reloads page when GPU memory is exhausted
    document.addEventListener('DOMContentLoaded', function() {
        setTimeout(function() {
            var canvas = document.querySelector('canvas');
            if (canvas) {
                canvas.addEventListener('webglcontextlost', function(e) {
                    e.preventDefault();
                    console.error('[WEBGL CONTEXT LOST] GPU memory exhausted. The model may be too large for this device.');
                }, false);

                canvas.addEventListener('webglcontextrestored', function() {
                    console.info('[WEBGL CONTEXT RESTORED] Reloading...');
                    window.location.reload();
                }, false);
            }
        }, 1000);
    });
}

import * as THREE from 'three';
import anime from 'animejs';
import * as state from './state.js';
import {
    markers,
    camera,
    controls,
    renderer,
    raycaster,
    occludeTargets,
    frustum,
    cameraViewProjectionMatrix,
    lastMarkerUpdate,
    setLastMarkerUpdate,
    MARKER_UPDATE_INTERVAL,
    _tempVector3,
    _tempVector3_2,
    _tempVector2,
    sideMats,
    sideMeshes,
    originalMaterials,
    graphicLayers,
    graphicLayerMap,
    uploadedGraphics,
    currentEditingGraphic,
    lockedGroupDecals,
    pendingGroupTexture,
    groupDecalSettings,
    decalSettings,
    activeGroup,
    activeSide,
    activeSideKey,
    setActiveSide,
    setActiveGroup,
    setActiveSideKey,
    setPendingGroupTexture,
    setCurrentEditingGraphic,
    setCachedCamoTexture,
    containerEl,
    viewportButtonsMap,
    scene
} from './state.js';

import {
    resetSideColor,
    updateSideMaterials
} from './materialManager.js';

import {
    updateAllMaterialsWithGraphicLayers,
    showDecalHint,
    invalidateBorderPointsCache
} from './decalManager.js';

import {
    initSizeSelector,
    rebuildOccludeTargets
} from './modelManager.js';

import {
    requestRender,
    textureLoader,
    createFakeShadow
} from './sceneManager.js';

import {
    getLabelText,
    initCfgSlideItems,
    getMaterialsArray
} from './utils.js';

// Helper: normalize side/group keys to a common (German) form for sorting
function normalizeKey(name) {
    const n = (name || '').trim().toLowerCase();
    switch (n) {
        case 'base':
            return 'basis';
        case 'glass':
            return 'glas';
        case 'terrace':
            return 'terrasse';
        case 'railing':
            return 'geländer';
        case 'attic':
            return 'attika';
        default:
            return n;
    }
}

// ===== MARKER UPDATE =====
export function updateMarkers(force = false) {
    if (!markers.size || !renderer || !camera) {
        return;
    }

    const now = performance.now();
    if (!force && (now - lastMarkerUpdate < MARKER_UPDATE_INTERVAL)) return;
    setLastMarkerUpdate(now);


    camera.updateMatrixWorld(true);
    camera.updateProjectionMatrix();

    cameraViewProjectionMatrix.multiplyMatrices(
        camera.projectionMatrix,
        camera.matrixWorldInverse
    );
    frustum.setFromProjectionMatrix(cameraViewProjectionMatrix);

    const canvas = renderer.domElement;
    const rect = canvas.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;


    const markerArray = [];
    const shiftPx = (window.innerWidth >= 768) ? 150 : 0;
    const visibleMarkers = new Set();

    markers.forEach(({empty, el}) => {
        if (empty.parent) {
            empty.parent.updateMatrixWorld(true);
        }
        empty.updateMatrixWorld(true);

        const pos = empty.getWorldPosition(_tempVector3);

        if (!frustum.containsPoint(pos)) {
            if (!el.classList.contains('hidden')) {
                el.classList.add('hidden');
            }
            return;
        }

        _tempVector3_2.copy(pos).project(camera);
        const v = _tempVector3_2;

        if (v.z > 1 || v.z < -1) {
            if (!el.classList.contains('hidden')) {
                el.classList.add('hidden');
            }
            return;
        }

        const x = (v.x * 0.5 + 0.5) * width + shiftPx;
        const y = (-v.y * 0.5 + 0.5) * height;

        el.style.transform = `translate(${x}px, ${y}px)`;

        _tempVector2.set(v.x, v.y);
        raycaster.setFromCamera(_tempVector2, camera);

        if (occludeTargets.length > 0) {
            const hits = raycaster.intersectObjects(occludeTargets, true);
            if (!hits.length) {
                visibleMarkers.add(el);
                if (el.classList.contains('hidden')) {
                    el.classList.remove('hidden');
                }
            } else {
                const distHit = hits[0].distance;
                const distMarker = camera.position.distanceTo(pos);
                if (distHit < distMarker - 1e-3) {
                    if (!el.classList.contains('hidden')) {
                        el.classList.add('hidden');
                    }
                } else {
                    visibleMarkers.add(el);
                    if (el.classList.contains('hidden')) {
                        el.classList.remove('hidden');
                    }
                }
            }
        } else {
            visibleMarkers.add(el);
            if (el.classList.contains('hidden')) {
                el.classList.remove('hidden');
            }
        }

        const dist = camera.position.distanceTo(pos);
        markerArray.push({el, dist});
    });

    if (markerArray.length > 0) {
        markerArray.sort((a, b) => b.dist - a.dist);
        markerArray.forEach((m, i) => {
            const newZIndex = (2 + i).toString();
            if (m.el.style.zIndex !== newZIndex) {
                m.el.style.zIndex = newZIndex;
            }
        });
    }
}

// ===== HELPER FUNCTIONS =====

/**
 * Opens RAL color selection modal
 * @param {Function} onDone - Callback function when color is selected
 */
function openRalModal(onDone) {
    // Get GLOBALS
    const GLOBALS = window.GLOBALS || { tc: {} };

    // Create modal
    const modal = document.createElement('div');
    modal.className = 'ral-modal-overlay';
    modal.innerHTML = `
        <div class="ral-modal">
            <div class="ral-modal-header">
                <h3>${GLOBALS.tc.ral_color || 'RAL-Farbe'}</h3>
                <button type="button" class="ral-close-btn">&times;</button>
            </div>
            <div class="ral-modal-body">
                <input type="text" class="ral-search" placeholder="${GLOBALS.tc.search_ral || 'RAL suchen...'}">
                <div class="ral-colors"></div>
            </div>
        </div>
    `;

    document.body.appendChild(modal);

    const ralColors = {
        '1000': '#BEBD7F', '1001': '#C2B078', '1002': '#C6A664',
        '1003': '#E5BE01', '1004': '#CDA434', '1005': '#A98307',
        '1006': '#E4A010', '1007': '#DC9D00', '2000': '#ED760E',
        '2001': '#C93C20', '2002': '#CB2821', '2003': '#FF7514',
        '2004': '#F44611', '3000': '#AF2B1E', '3001': '#A52019',
        '3002': '#A2231D', '3003': '#9B111E', '3004': '#75151E',
        '3005': '#5E2129', '3007': '#412227', '4001': '#6D3F5B',
        '4002': '#922B3E', '4003': '#DE4C8A', '4004': '#641C34',
        '4005': '#6C4675', '4006': '#A03472', '4007': '#4A192C',
        '4008': '#924E7D', '4009': '#A18594', '5000': '#354D73',
        '5001': '#1F3438', '5002': '#20214F', '5003': '#1D1E33',
        '5004': '#18171C', '5005': '#1E2460', '5007': '#3E5F8A',
        '5008': '#26252D', '5009': '#025669', '5010': '#0E4C8A',
        '5011': '#231A24', '5012': '#3B83BD', '5013': '#1E213D',
        '5014': '#606E8C', '5015': '#2271B3', '5017': '#063971',
        '5018': '#3F888F', '5019': '#1B5583', '5020': '#1D334A',
        '5021': '#256D7B', '5022': '#252850', '5023': '#49678D',
        '5024': '#5D9B9B', '6000': '#316650', '6001': '#287233',
        '6002': '#2D572C', '6003': '#424632', '6004': '#1F3A3D',
        '6005': '#2F4538', '6006': '#3E3B32', '6007': '#343B29',
        '6008': '#39352A', '6009': '#31372B', '6010': '#35682D',
        '6011': '#587246', '6012': '#343E40', '6013': '#6C7156',
        '6014': '#47402E', '6015': '#3B3C36', '6016': '#1E5945',
        '6017': '#4C9141', '6018': '#57A639', '6019': '#BDECB6',
        '6020': '#2E3A23', '6021': '#89AC76', '6022': '#25221B',
        '6024': '#308446', '6025': '#3D642D', '6026': '#015D52',
        '6027': '#84C3BE', '6028': '#2C5545', '6029': '#20603D',
        '6032': '#317F43', '6033': '#497E76', '6034': '#7FB5B5',
        '7000': '#78858B', '7001': '#8A9597', '7002': '#7E7B52',
        '7003': '#6C7059', '7004': '#969992', '7005': '#646B63',
        '7006': '#6D6552', '7008': '#6A5F31', '7009': '#4D5645',
        '7010': '#4C514A', '7011': '#434B4D', '7012': '#4E5754',
        '7013': '#464531', '7015': '#434750', '7016': '#293133',
        '7021': '#23282B', '7022': '#332F2C', '7023': '#686C5E',
        '7024': '#474A51', '7026': '#2F353B', '7030': '#8B8C7A',
        '7031': '#474B4E', '7032': '#B8B799', '7033': '#7D8471',
        '7034': '#8F8B66', '7035': '#D7D7D7', '7036': '#7F7679',
        '7037': '#7D7F7D', '7038': '#B5B8B1', '7039': '#6C6960',
        '7040': '#9DA1AA', '7042': '#8D948D', '7043': '#4E5452',
        '7044': '#CAC4B0', '8000': '#826C34', '8001': '#955F20',
        '8002': '#6C3B2A', '8003': '#734222', '8004': '#8E402A',
        '8007': '#59351F', '8008': '#6F4F28', '8011': '#5B3A29',
        '8012': '#592321', '8014': '#382C1E', '8015': '#633A34',
        '8016': '#4C2F27', '8017': '#45322E', '8019': '#403A3A',
        '8022': '#212121', '8023': '#A65E2E', '8024': '#79553D',
        '8025': '#755C48', '8028': '#4E3B31', '9001': '#FDF4E3',
        '9002': '#E7EBDA', '9003': '#F4F4F4', '9004': '#282828',
        '9005': '#0A0A0A', '9006': '#A5A5A5', '9007': '#8F8F8F',
        '9010': '#FFFFFF', '9011': '#1C1C1C', '9016': '#F6F6F6',
        '9017': '#1E1E1E', '9018': '#D7D7D7'
    };

    const ralContainer = modal.querySelector('.ral-colors');
    const searchInput = modal.querySelector('.ral-search');
    const closeBtn = modal.querySelector('.ral-close-btn');

    function renderColors(filter = '') {
        ralContainer.innerHTML = '';
        Object.entries(ralColors).forEach(([code, hex]) => {
            if (filter && !code.includes(filter)) return;
            const swatch = document.createElement('div');
            swatch.className = 'ral-swatch';
            swatch.style.background = hex;
            swatch.title = `RAL ${code}`;
            swatch.innerHTML = `<span>${code}</span>`;
            swatch.addEventListener('click', () => {
                onDone(hex);
                close();
            });
            ralContainer.appendChild(swatch);
        });
    }

    function close() {
        modal.remove();
    }

    searchInput.addEventListener('input', (e) => {
        renderColors(e.target.value);
    });

    closeBtn.addEventListener('click', close);
    modal.addEventListener('click', (e) => {
        if (e.target === modal) close();
    });

    renderColors();
}

// WeakMap to store callback functions for each container
const swatchCallbacks = new WeakMap();

/**
 * Add custom color swatches to a color-swatches container
 */
function addCustomColorSwatches(container, onColorSelect) {
    // Store the callback for later use
    if (onColorSelect) {
        swatchCallbacks.set(container, onColorSelect);
    }

    // Remove existing custom color swatches
    container.querySelectorAll('.swatch.custom-color').forEach(s => s.remove());

    // Add new custom color swatches
    const customColors = Array.from(state.customColors);
    customColors.forEach(hexColor => {
        const swatch = document.createElement('div');
        swatch.className = 'swatch custom-color';
        swatch.style.background = hexColor;
        swatch.dataset.color = hexColor;
        swatch.title = `Eigene Farbe: ${hexColor}`;

        swatch.addEventListener('click', () => {
            // Alle Swatches (inkl. Standard-Swatches) auf inaktiv setzen
            container.querySelectorAll('.swatch').forEach(s => s.classList.remove('active'));
            // Diesen Swatch aktivieren
            swatch.classList.add('active');

            if (onColorSelect) {
                onColorSelect(hexColor);
            }
        });

        // Insert at the beginning (custom colors first)
        container.prepend(swatch);
    });
}

// Aktualisiert alle custom color swatches in allen offenen Editoren
function refreshAllCustomColorSwatches() {
    document.querySelectorAll('.color-swatches').forEach(container => {
        // Get stored callback for this container
        const onColorSelect = swatchCallbacks.get(container);

        // Remove existing custom color swatches
        container.querySelectorAll('.swatch.custom-color').forEach(s => s.remove());

        // Add updated custom color swatches with event listeners
        const customColors = Array.from(state.customColors);
        customColors.forEach(hexColor => {
            const swatch = document.createElement('div');
            swatch.className = 'swatch custom-color';
            swatch.style.background = hexColor;
            swatch.dataset.color = hexColor;
            swatch.title = `Eigene Farbe: ${hexColor}`;

            // Add click handler if callback exists
            if (onColorSelect) {
                swatch.addEventListener('click', () => {
                    container.querySelectorAll('.swatch').forEach(s => s.classList.remove('active'));
                    swatch.classList.add('active');
                    onColorSelect(hexColor);
                });
            }

            // Insert at the beginning (custom colors first)
            container.prepend(swatch);
        });
    });
}

/**
 * Updates marker UI - stub function
 */
function updateMarkerUI() {
    // This function would update the visual state of markers
    // Currently a stub as the implementation depends on the specific UI requirements
}

/**
 * Enhances CFG slide item synchronization
 */
function enhanceCfgSlideSync() {
    // Funktion zum Löschen nicht-platzierter Grafiken
    function deleteUnplacedGraphics() {
        // Sammle alle Grafiken, die nicht platziert wurden
        const unplacedGraphics = uploadedGraphics.filter(graphic => {
            const layerIndex = graphicLayerMap.get(graphic.id);
            return layerIndex === undefined; // Nicht platziert = kein Layer-Eintrag
        });

        // Lösche alle nicht-platzierten Grafiken
        unplacedGraphics.forEach(graphic => {
            // Grafik aus Array entfernen
            const graphicIndex = uploadedGraphics.findIndex(g => g.id === graphic.id);
            if (graphicIndex > -1) {
                uploadedGraphics.splice(graphicIndex, 1);
            }

            // Grafik vom Modell entfernen
            if (pendingGroupTexture && pendingGroupTexture.graphicId === graphic.id) {
                setPendingGroupTexture(null);
            }

            if (activeGroup === graphic.id) {
                setActiveGroup(null);
            }

            // Remove from layer system
            const layerIndex = graphicLayerMap.get(graphic.id);
            if (layerIndex !== undefined) {
                graphicLayers[layerIndex] = null;
                graphicLayerMap.delete(graphic.id);
            }

            // Update all materials to reflect removed layer
            updateAllMaterialsWithGraphicLayers();

            groupDecalSettings.delete(graphic.id);
            decalSettings.delete(graphic.id);
            lockedGroupDecals.delete(graphic.id);

            // UI Row entfernen
            const panelContainer = document.querySelector('.configuratorPanel');
            const row = panelContainer?.querySelector(`.graphic-row[data-graphic-id="${graphic.id}"]`);
            if (row) row.remove();

            // currentEditingGraphic zurücksetzen falls nötig
            if (currentEditingGraphic?.id === graphic.id) {
                setCurrentEditingGraphic(null);
            }
        });

        // Upload-Feld wieder hinzufügen wenn Platz ist
        if (unplacedGraphics.length > 0 && uploadedGraphics.length < 8) {
            const graphicsUploadContainer = document.querySelector('.graphics-upload-container');
            if (graphicsUploadContainer && !graphicsUploadContainer.querySelector('.graphic-upload-field')) {
                const createUploadField = window._createUploadField;
                if (createUploadField) {
                    graphicsUploadContainer.appendChild(createUploadField(uploadedGraphics.length));
                }
            }
        }

        requestRender();
    }

    const cfgSlideItems = document.querySelectorAll('.cfgSlideItem');

    cfgSlideItems.forEach(item => {
        const headline = item.querySelector('.cfgSlideItem__headline');
        if (!headline || headline.__syncEnhanced) return;

        headline.__syncEnhanced = true;

        headline.addEventListener('click', () => {
            const content = item.querySelector('.cfgSlideItem__content');
            const isOpen = item.classList.contains('is-active');

            // Wenn ein NICHT-Graphics-Slide geklickt wird, lösche nicht-platzierte Grafiken
            if (item.dataset.type !== 'graphics-upload') {
                deleteUnplacedGraphics();
            }

            if (isOpen) {
                // Close
                item.classList.remove('is-active');
                if (content) {
                    content.style.display = 'none';
                }
                // Dispatch custom event
                const event = new CustomEvent('cfgslide:closed', { bubbles: true });
                item.dispatchEvent(event);
            } else {
                // Close all others first
                cfgSlideItems.forEach(otherItem => {
                    if (otherItem !== item && otherItem.classList.contains('is-active')) {
                        otherItem.classList.remove('is-active');
                        const otherContent = otherItem.querySelector('.cfgSlideItem__content');
                        if (otherContent) {
                            otherContent.style.display = 'none';
                        }
                        const closeEvent = new CustomEvent('cfgslide:closed', { bubbles: true });
                        otherItem.dispatchEvent(closeEvent);
                    }
                });

                // Open this one
                item.classList.add('is-active');
                if (content) {
                    content.style.display = 'block';
                }
                // Dispatch custom event
                const event = new CustomEvent('cfgslide:opened', { bubbles: true });
                item.dispatchEvent(event);
            }
        });
    });
}

/**
 * Focuses camera on a specific side of the model
 * @param {string} side - The side identifier
 * @param {string} group - The group identifier
 */
function focusCameraOnSide(side, group) {
    // Find the meshes for this side
    const meshesForSide = sideMeshes.get(group)?.get(side);

    if (!meshesForSide || meshesForSide.size === 0) {
        return;
    }

    // Calculate bounding box of all meshes for this side
    const boundingBox = new THREE.Box3();
    meshesForSide.forEach(mesh => {
        const meshBox = new THREE.Box3().setFromObject(mesh);
        boundingBox.union(meshBox);
    });

    // Get center of the bounding box
    const targetPosition = new THREE.Vector3();
    boundingBox.getCenter(targetPosition);

    // Calculate appropriate distance based on bounding box size
    const boundingBoxSize = new THREE.Vector3();
    boundingBox.getSize(boundingBoxSize);
    const maxDimension = Math.max(boundingBoxSize.x, boundingBoxSize.y, boundingBoxSize.z);

    // Base distance: 2.5x the largest dimension of the bounding box
    const baseDistance = maxDimension * 2.5;

    // Use current distance if it's reasonable, otherwise use base distance
    const currentDistance = camera.position.distanceTo(controls.target);
    const targetDistance = Math.min(currentDistance, baseDistance * 1.5);

    // Calculate normals weighted by area, filtering out back-facing surfaces
    // Step 1: Find largest face to use as reference direction
    let largestFaceNormal = null;
    let largestFaceArea = 0;
    const allFaces = [];

    meshesForSide.forEach(mesh => {
        if (mesh.geometry && mesh.geometry.index) {
            const posAttr = mesh.geometry.attributes.position;
            const index = mesh.geometry.index;
            const normalMatrix = new THREE.Matrix3().getNormalMatrix(mesh.matrixWorld);

            const v0 = new THREE.Vector3();
            const v1 = new THREE.Vector3();
            const v2 = new THREE.Vector3();
            const cb = new THREE.Vector3();
            const ab = new THREE.Vector3();

            // Collect all faces with their normals and areas
            for (let i = 0; i < index.count; i += 3) {
                v0.fromBufferAttribute(posAttr, index.array[i]);
                v1.fromBufferAttribute(posAttr, index.array[i + 1]);
                v2.fromBufferAttribute(posAttr, index.array[i + 2]);

                cb.subVectors(v2, v1);
                ab.subVectors(v0, v1);
                const faceNormal = new THREE.Vector3().crossVectors(cb, ab);

                const area = faceNormal.length() * 0.5;
                faceNormal.normalize();
                faceNormal.applyMatrix3(normalMatrix);

                allFaces.push({ normal: faceNormal, area: area });

                // Track largest face
                if (area > largestFaceArea) {
                    largestFaceArea = area;
                    largestFaceNormal = faceNormal.clone();
                }
            }
        }
    });

    let averageNormal;
    if (largestFaceNormal && allFaces.length > 0) {
        // Step 2: Filter faces that point in similar direction (< 90° angle)
        let weightedNormal = new THREE.Vector3(0, 0, 0);
        let totalArea = 0;

        allFaces.forEach(face => {
            // Only include faces pointing in roughly same direction as largest face
            // Use -0.3 threshold to allow slightly backwards-facing surfaces (< 107° angle)
            const dotProduct = face.normal.dot(largestFaceNormal);
            if (dotProduct > -0.3) {
                weightedNormal.add(face.normal.clone().multiplyScalar(face.area));
                totalArea += face.area;
            }
        });

        if (totalArea > 0) {
            averageNormal = weightedNormal.divideScalar(totalArea).normalize();
        } else {
            // Fallback to largest face normal if filtering removed everything
            averageNormal = largestFaceNormal;
        }
    } else {
        // Fallback: use direction from target to current camera
        averageNormal = camera.position.clone().sub(controls.target).normalize();
    }

    // Calculate new camera position along the normal direction
    // Test BOTH directions to find the one with better visibility
    let newCameraPosition;
    const maxAttempts = 3;

    // Get all meshes except the ones we're focusing on (to check for obstructions)
    // Exclude floor and other background objects
    const allSceneMeshes = [];
    state.scene.traverse(obj => {
        if (obj.isMesh &&
            !meshesForSide.has(obj) &&
            obj !== state.floor &&
            !obj.name.toLowerCase().includes('floor') &&
            !obj.name.toLowerCase().includes('ground') &&
            !obj.name.toLowerCase().includes('background')) {
            allSceneMeshes.push(obj);
        }
    });

    // Test both normal and inverted normal direction
    const directionsToTest = [
        { normal: averageNormal.clone(), name: 'normal' },
        { normal: averageNormal.clone().negate(), name: 'inverted' }
    ];

    let bestPosition = null;
    let bestClearPercentage = 0;
    let bestMultiplier = 1.0;

    for (const directionTest of directionsToTest) {
        let distanceMultiplier = 1.0;

        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            const testDistance = targetDistance * distanceMultiplier;
            const testPosition = targetPosition.clone().add(
                directionTest.normal.clone().multiplyScalar(testDistance)
            );

            // Multi-point sampling: Test multiple points on the target surface
            // to handle partial obstructions (terraces, balconies, etc.)
            const samplePoints = [
                targetPosition.clone(), // Center
                targetPosition.clone().add(new THREE.Vector3(boundingBoxSize.x * 0.3, 0, 0)),
                targetPosition.clone().add(new THREE.Vector3(-boundingBoxSize.x * 0.3, 0, 0)),
                targetPosition.clone().add(new THREE.Vector3(0, boundingBoxSize.y * 0.3, 0)),
                targetPosition.clone().add(new THREE.Vector3(0, -boundingBoxSize.y * 0.3, 0))
            ];

            let clearSamples = 0;
            const totalSamples = samplePoints.length;

            for (const samplePoint of samplePoints) {
                const direction = samplePoint.clone().sub(testPosition).normalize();
                const distance = testPosition.distanceTo(samplePoint);

                raycaster.set(testPosition, direction);
                raycaster.far = distance * 0.95; // Stop just before target

                const hits = raycaster.intersectObjects(allSceneMeshes, true);

                if (hits.length === 0) {
                    clearSamples++;
                }
            }

            // Calculate visibility percentage
            const clearPercentage = clearSamples / totalSamples;

            // Track best position across all directions and attempts
            if (clearPercentage > bestClearPercentage) {
                bestClearPercentage = clearPercentage;
                bestPosition = testPosition;
                bestMultiplier = distanceMultiplier;
            }

            // If we found a very good view (>80%), stop testing this direction
            if (clearPercentage > 0.8) {
                break;
            }

            // Try slightly further away (smaller increments for stability)
            distanceMultiplier *= 1.15;
        }
    }

    // Use the best position found, or fallback to base distance
    if (bestPosition) {
        newCameraPosition = bestPosition;
    } else {
        // Ultimate fallback: use base distance along average normal
        newCameraPosition = targetPosition.clone().add(
            averageNormal.multiplyScalar(targetDistance)
        );
    }

    // Animate camera and controls
    const startPosition = camera.position.clone();
    const startTarget = controls.target.clone();

    anime({
        targets: { t: 0 },
        t: 1,
        duration: 1000,
        easing: 'easeInOutQuad',
        update: function(anim) {
            const t = anim.animations[0].currentValue;

            // Interpolate camera position
            camera.position.lerpVectors(startPosition, newCameraPosition, t);

            // Interpolate controls target
            controls.target.lerpVectors(startTarget, targetPosition, t);

            // Update controls
            controls.update();

            // Request render
            state.setNeedsRender(true);
        }
    });
}

// Helper function to be called from dropdown
function animateCameraToSide(group, side) {
    focusCameraOnSide(side, group);
}

// ===== MOBILE TABS INITIALIZATION =====
function initMobileTabs() {
    // Nur auf Mobile anwenden
    if (window.innerWidth > 767) return; // $screen-xs-max

    const panelContainer = document.querySelector('.configuratorPanel');
    if (!panelContainer) return;

    const slideItems = panelContainer.querySelectorAll('.cfgSlideItem');
    if (slideItems.length === 0) return;

    // Tab-Bar erstellen
    const tabBar = document.createElement('div');
    tabBar.className = 'mobile-tab-bar';

    // Tab-Content Container
    const tabContent = document.createElement('div');
    tabContent.className = 'mobile-tab-content';

    // Slides verarbeiten
    slideItems.forEach((slide, index) => {
        const headline = slide.querySelector('.cfgSlideItem__headline');
        const content = slide.querySelector('.cfgSlideItem__content');

        if (!headline || !content) return;

        // Tab-Button erstellen
        const tab = document.createElement('button');
        tab.className = 'mobile-tab';
        tab.textContent = headline.textContent;
        tab.dataset.index = index;

        // Erster Tab ist aktiv
        if (index === 0 || slide.classList.contains('is-active')) {
            tab.classList.add('is-active');
            content.style.display = 'block';
        } else {
            content.style.display = 'none';
        }

        // Tab Click Handler
        tab.addEventListener('click', () => {
            // Exit graphic edit mode when switching tabs
            if (uploadedGraphics) {
                uploadedGraphics.forEach(graphic => {
                    if (graphic.isEditing) {
                        graphic.isEditing = false;
                        setCurrentEditingGraphic(null);
                        lockedGroupDecals.set(graphic.id, true);

                        // Update panel UI
                        const graphicRow = panelContainer?.querySelector(`.graphic-row[data-graphic-id="${graphic.id}"]`);
                        if (graphicRow) {
                            const controls = graphicRow.querySelector('.graphic-controls');
                            const editBtn = graphicRow.querySelector('.edit-graphic-btn');
                            if (controls) controls.style.display = 'none';
                            if (editBtn) editBtn.innerHTML = '<i class="icon icon-edit"></i>';
                            graphicRow.style.flexWrap = '';
                        }

                        // Remove viewport buttons
                        const buttonsToRemove = viewportButtonsMap.get(graphic.id);
                        if (buttonsToRemove) {
                            buttonsToRemove.confirmBtn.remove();
                            buttonsToRemove.deleteBtn.remove();
                            viewportButtonsMap.delete(graphic.id);
                        }

                        // Remove 3D frame from scene
                        const frameKey = `decal-frame-${graphic.id}`;
                        const frame3D = scene.getObjectByName(frameKey);
                        if (frame3D) {
                            scene.remove(frame3D);
                            frame3D.geometry.dispose();
                            frame3D.material.dispose();
                        }
                    }
                });
                updateAllMaterialsWithGraphicLayers();
                requestRender();
            }

            // Alle Tabs deaktivieren
            tabBar.querySelectorAll('.mobile-tab').forEach(t => {
                t.classList.remove('is-active');
            });

            // Alle Contents verstecken
            slideItems.forEach(s => {
                const c = s.querySelector('.cfgSlideItem__content');
                if (c) c.style.display = 'none';
                s.classList.remove('is-active');
            });

            // Aktiven Tab aktivieren
            tab.classList.add('is-active');
            content.style.display = 'block';
            slide.classList.add('is-active');
        });

        tabBar.appendChild(tab);

        // Headline verstecken (wird durch Tab ersetzt)
        headline.style.display = 'none';

        // Content zum Container hinzufügen
        tabContent.appendChild(slide);
    });

    // Struktur aufbauen
    panelContainer.innerHTML = '';
    panelContainer.appendChild(tabBar);
    panelContainer.appendChild(tabContent);
}

// ===== BUILD UI FROM MARKERS =====
/**
 * Generates the configurator panel UI from 3D model markers
 * Creates color/material swatches, decal controls, and the entire UI structure
 * @param {Map} groups - The configuration groups from window._configGroups
 */
export function buildUIFromMarkers(groups = window._configGroups) {
    try {
    const GLOBALS = window.GLOBALS || { tc: {} };

        const panelContainer = document.querySelector('.configuratorPanel');
        if (!panelContainer) return;

        // Graphics-Upload Slide sichern (wird später wiederverwendet)
        const existingGraphicsSlide = panelContainer.querySelector('.cfgSlideItem[data-type="graphics-upload"]');
        if (existingGraphicsSlide) {
            existingGraphicsSlide.remove();
        }

        // Alle anderen konfigurierbaren Slides löschen
        panelContainer.querySelectorAll('.cfgSlideItem[data-side], .cfgSlideItem[data-group], .cfgSlideItem[data-type]').forEach(el => el.remove());

        let existingSizeOptions = panelContainer.querySelector('.sizeOptions');

        if (!existingSizeOptions && window._sizeOptionsBackup) {
            existingSizeOptions = window._sizeOptionsBackup.cloneNode(true);
        }

        if (existingSizeOptions && !window._sizeOptionsBackup) {
            window._sizeOptionsBackup = existingSizeOptions.cloneNode(true);
        }

        const oldSizeSlide = panelContainer.querySelector('#modelSizeSelector');
        if (oldSizeSlide) oldSizeSlide.remove();

        // Track whether size slide was shown
        let hasSizeSlide = false;

        // 1. Modellgröße - initial ausgeklappt (nur wenn mehr als eine Option)
        if (existingSizeOptions) {
            // Zähle die Anzahl der Optionen (Buttons/Radios)
            const optionButtons = existingSizeOptions.querySelectorAll('button, input[type="radio"], .sizeOption');
            const optionCount = optionButtons.length;

            // Nur anzeigen wenn mehr als eine Option vorhanden ist
            if (optionCount > 1) {
                hasSizeSlide = true;
                const sizeSlide = document.createElement('div');
                sizeSlide.className = 'cfgSlideItem is-active';
                sizeSlide.id = 'modelSizeSelector';
                sizeSlide.innerHTML = `
                <div class="cfgSlideItem__headline">${GLOBALS.tc.model_size || 'Modellgröße'}</div>
                <div class="cfgSlideItem__content" style="display: block;"></div>
            `;
                const contentContainer = sizeSlide.querySelector('.cfgSlideItem__content');
                contentContainer.appendChild(existingSizeOptions);

                const panelHeader = panelContainer.querySelector('.panelHeader');
                if (panelHeader) {
                    panelHeader.insertAdjacentElement('afterend', sizeSlide);
                } else {
                    panelContainer.prepend(sizeSlide);
                }
            }
        }

        // 2. Farbauswahl Slide
        if (groups) {
            // Wenn keine Größenauswahl angezeigt wird, öffne Farbauswahl initial
            const shouldOpenColorSlide = !hasSizeSlide;

            const colorSlide = document.createElement('div');
            colorSlide.className = shouldOpenColorSlide ? 'cfgSlideItem is-active' : 'cfgSlideItem';
            colorSlide.dataset.type = 'color-selection';
            colorSlide.innerHTML = `
                <div class="cfgSlideItem__headline">${GLOBALS.tc.color_picker || 'Farbauswahl'}</div>
                <div class="cfgSlideItem__content" style="display:${shouldOpenColorSlide ? 'block' : 'none'};">
                <div class="inputGroup">
                    <div class="selectBody">
                        <select class="side-group-switch">
                            <option value="groups">${GLOBALS.tc.all_groups || 'Alle Seiten'}</option>
                        </select>
                    </div>
                </div>
                    <div class="groups-list"></div>
                    <div class="sides-list" style="display:none;"></div>
                </div>
            `;

            const groupsList = colorSlide.querySelector('.groups-list');
            const sidesList = colorSlide.querySelector('.sides-list');

            const switcher = colorSlide.querySelector('.side-group-switch');

            // Configurator panel-wide guards to prevent unintended navigation on iOS
            // 1) Block dummy anchors inside the panel (href="#", empty, javascript:)
            const anchorGuard = (ev) => {
                const a = ev.target.closest('a');
                if (!a) return;
                const href = (a.getAttribute('href') || '').trim().toLowerCase();
                if (href === '' || href === '#' || href.startsWith('javascript:')) {
                    ev.preventDefault();
                    ev.stopPropagation();
                }
            };
            colorSlide.addEventListener('click', anchorGuard, true);
            colorSlide.addEventListener('touchstart', anchorGuard, true);

            // 2) Stop bubbling for known interactive controls in the color editor
            //    Only stop propagation (do NOT preventDefault) and only on click,
            //    so iOS can still synthesize the click event from touch.
            //    IMPORTANT: Do NOT stop propagation for nativeColorBtn - it needs to trigger the input
            const bubbleGuard = (ev) => {
                if (ev.target.closest('.ralColorBtn, .edit-btn, .reset-btn, .color-swatch')) {
                    ev.stopPropagation();
                }
                // Removed .nativeColorBtn from here - it needs events to flow to the color input
            };
            colorSlide.addEventListener('click', bubbleGuard, false);

            // Sortiere Dropdown-Optionen nach gewünschter Reihenfolge
            const groupOrder = ['basis', 'trailer', 'attika', 'geländer', 'terrasse', 'glas'];
            const sortedGroupsForDropdown = Array.from(groups.entries()).sort(([nameA], [nameB]) => {
                const indexA = groupOrder.findIndex(g => normalizeKey(nameA).includes(g));
                const indexB = groupOrder.findIndex(g => normalizeKey(nameB).includes(g));
                if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                if (indexA !== -1) return -1;
                if (indexB !== -1) return 1;
                return nameA.localeCompare(nameB);
            });

            for (const [groupName, groupData] of sortedGroupsForDropdown) {
                if (!groupData.sides) continue;

                // Sortiere Seiten innerhalb der Gruppe für Dropdown
                const sideOrder = ['basis', 'trailer', 'attika', 'geländer', 'terrasse', 'glas'];
                const sortedSidesForDropdown = Array.from(groupData.sides.entries()).sort(([nameA], [nameB]) => {
                    const indexA = sideOrder.findIndex(s => normalizeKey(nameA).includes(s));
                    const indexB = sideOrder.findIndex(s => normalizeKey(nameB).includes(s));
                    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                    if (indexA !== -1) return -1;
                    if (indexB !== -1) return 1;
                    return nameA.localeCompare(nameB);
                });

                for (const [sideName] of sortedSidesForDropdown) {
                    const option = document.createElement('option');
                    option.value = `${groupName}:${sideName}`;
                    option.textContent = getLabelText(groupName, sideName);
                    switcher.appendChild(option);
                }
            }

            // Prüfe ob es Gruppen mit mehreren Seiten gibt (ohne default)
            let hasMultiSideGroups = false;
            for (const [groupName, groupData] of groups.entries()) {
                if (groupName !== 'default' && groupData.sides && groupData.sides.size > 1) {
                    hasMultiSideGroups = true;
                    break;
                }
            }

            // Gruppen auflisten - sortiert nach gewünschter Reihenfolge
            const groupOrderMain = ['basis', 'trailer', 'attika', 'geländer', 'terrasse', 'glas'];
            const sortedGroups = Array.from(groups.entries()).sort(([nameA], [nameB]) => {
                const normA = normalizeKey(nameA);
                const normB = normalizeKey(nameB);

                const aIsBasis = normA === 'basis';
                const bIsBasis = normB === 'basis';
                if (aIsBasis && !bIsBasis) return -1;
                if (bIsBasis && !aIsBasis) return 1;

                const indexA = groupOrderMain.findIndex(g => normA.includes(g));
                const indexB = groupOrderMain.findIndex(g => normB.includes(g));

                if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                if (indexA !== -1) return -1;
                if (indexB !== -1) return 1;

                return normA.localeCompare(normB);
            });

            for (const [groupName, groupData] of sortedGroups) {
                // Überspringe default-Gruppe immer (wird separat behandelt)
                if (groupName === 'default') continue;
                if (!groupData.mats || groupData.mats.size === 0) continue;

                // Wenn es Multi-Side-Gruppen gibt, überspringe Single-Side-Gruppen und No-Side-Gruppen
                // Wenn es KEINE gibt, zeige alle Gruppen mit Materials (auch ohne oder mit nur einer Seite)
                if (hasMultiSideGroups) {
                    if (!groupData.sides || groupData.sides.size <= 1) continue;
                }

                // Hole ursprüngliche Farbe aus dem ersten Material der Gruppe
                const firstMat = groupData.mats.values().next().value;
                const origColor = firstMat?.userData?.originalColor;
                const currentColor = groupData.color || (origColor ? `#${origColor.getHexString()}` : '#FFFFFF');

                // Prüfe ob Material "glas" im Namen hat (case insensitive)
                const isGlass = firstMat?.name?.toLowerCase().includes('glas') || false;

                const groupRow = document.createElement('div');
                groupRow.className = 'group-color-row';
                groupRow.dataset.group = groupName;

                // Swatches: 8 Standardfarben (Camo-Textur auskommentiert)
                const swatchesHTML = `
                    <div class="swatch" data-color="#ffffff" style="background:#ffffff; border: 1px solid #ddd;"></div>
                    <div class="swatch" data-color="#1C1C1C" style="background:#1C1C1C;"></div>
                    <div class="swatch" data-color="#E1E1E1" style="background:#E1E1E1;"></div>
                    <div class="swatch" data-color="#4A4A4A" style="background:#4A4A4A;"></div>
                    <div class="swatch" data-color="#C00000" style="background:#C00000;"></div>
                    <div class="swatch" data-color="#0D47A1" style="background:#0D47A1;"></div>
                    <div class="swatch" data-color="#FFD700" style="background:#FFD700;"></div>
                    <div class="swatch" data-color="#1B5E20" style="background:#1B5E20;"></div>
                `;

                groupRow.innerHTML = `
                    <div class="group-info">
                        <div class="color-swatch" style="background: ${currentColor};"></div>
                        <span class="group-label">${getLabelText(groupName)}</span>
                    </div>
                    <button type="button" class="edit-btn">
                        <i class="icon icon-edit"></i>
                    </button>
                    <button type="button" class="reset-btn">
                        <i class="icon icon-reload"></i>
                    </button>
                    <div class="color-editor">
                        <div class="color-swatches">
                            ${swatchesHTML}
                        </div>
                        <div class="custom-color-buttons">
                            <button type="button" class="nativeColorBtn">
                                ${GLOBALS.tc.custom_color || 'Individuell'} <i class="icon icon-edit"></i>
                            </button>
                            <button type="button" class="ralColorBtn">
                                ${GLOBALS.tc.ral_color || 'RAL-Farbe'} <i class="icon icon-edit"></i>
                            </button>

                            <!-- color input (positioniert unter dem Button) -->
                            <input type="color" class="nativeColorInput">
                        </div>
                    </div>
                `;

                groupsList.appendChild(groupRow);

                // Event Handlers für diese Gruppe
                const editBtn = groupRow.querySelector('.edit-btn');
                const resetBtn = groupRow.querySelector('.reset-btn');
                const colorEditor = groupRow.querySelector('.color-editor');
                const colorSwatchDisplay = groupRow.querySelector('.color-swatch');

                const toggleEditor = (forceOpen = false) => {
                    const isOpen = colorEditor.style.display === 'block';
                    if (forceOpen && isOpen) return;

                    const shouldOpen = forceOpen || !isOpen;

                    if (shouldOpen) {
                        document.querySelectorAll('.group-color-row, .side-color-row').forEach(row => {
                            if (row !== groupRow) {
                                const otherEditor = row.querySelector('.color-editor');
                                const otherEditBtn = row.querySelector('.edit-btn');
                                if (otherEditor && otherEditor.style.display === 'block') {
                                    otherEditor.style.display = 'none';
                                    if (otherEditBtn) otherEditBtn.innerHTML = '<i class="icon icon-edit"></i>';
                                    row.style.flexWrap = '';
                                }
                            }
                        });

                        colorEditor.style.display = 'block';
                        editBtn.innerHTML = '<i class="icon icon-check"></i>';
                        groupRow.style.flexWrap = 'wrap';
                        colorEditor.style.width = '100%';

                        // Load custom color swatches
                        const colorSwatches = colorEditor.querySelector('.color-swatches');
                        addCustomColorSwatches(colorSwatches, (hexColor) => {
                            applyGroupColor(groupName, hexColor);
                            colorSwatchDisplay.style.background = hexColor;
                            if (groupData) groupData.color = hexColor;
                            window._markConfigurationChanged();
                        });

                        // Markiere den aktuell ausgewählten Swatch als active
                        if (groupData && groupData.color) {
                            const currentColor = groupData.color;
                            colorEditor.querySelectorAll('.swatch').forEach(s => s.classList.remove('active'));

                            if (currentColor === 'camo') {
                                const camoSwatch = colorEditor.querySelector('.swatch[data-texture="camo"]');
                                if (camoSwatch) camoSwatch.classList.add('active');
                            } else {
                                const activeSwatch = colorEditor.querySelector(`.swatch[data-color="${currentColor}"]`);
                                if (activeSwatch) activeSwatch.classList.add('active');
                            }
                        }
                    } else {
                        colorEditor.style.display = 'none';
                        editBtn.innerHTML = '<i class="icon icon-edit"></i>';
                        groupRow.style.flexWrap = '';
                    }
                };

                editBtn.addEventListener('click', () => toggleEditor());
                colorSwatchDisplay.addEventListener('click', () => toggleEditor(true));

                const groupLabel = groupRow.querySelector('.group-label');
                if (groupLabel) {
                    groupLabel.style.cursor = 'pointer';
                    groupLabel.addEventListener('click', () => toggleEditor(true));
                }

                resetBtn.addEventListener('click', () => {
                    // Reset zum ursprünglichen Material
                    resetGroupToOriginal(groupName);
                    // Farbe der Color Swatch auf ursprüngliche Farbe setzen
                    const origColor = groupData.mats.values().next().value?.userData?.originalColor;
                    const displayColor = origColor ? `#${origColor.getHexString()}` : '#FFFFFF';
                    colorSwatchDisplay.style.background = displayColor;
                    if (groupData) groupData.color = null;
                    // Entferne active Klasse von allen Swatches
                    groupRow.querySelectorAll('.swatch').forEach(s => s.classList.remove('active'));
                });

                // Swatches im Editor
                groupRow.querySelectorAll('.swatch').forEach(swatch => {
                    swatch.addEventListener('click', () => {
                        groupRow.querySelectorAll('.swatch').forEach(s => s.classList.remove('active'));
                        swatch.classList.add('active');

                        if (swatch.dataset.texture === 'camo') {
                            applyGroupCamoTexture(groupName);
                            colorSwatchDisplay.style.backgroundImage = 'url(/userdata/files/models/camo-texture.jpg)';
                            colorSwatchDisplay.style.backgroundSize = 'cover';
                            colorSwatchDisplay.style.backgroundColor = '';
                            if (groupData) groupData.color = 'camo';
                            window._markConfigurationChanged();
                        } else {
                            const color = swatch.dataset.color;
                            applyGroupColor(groupName, color);
                            colorSwatchDisplay.style.background = color;
                            colorSwatchDisplay.style.backgroundImage = '';
                            if (groupData) groupData.color = color;
                            window._markConfigurationChanged();
                        }
                    });
                });

                // Native Color Picker
                const nativeColorBtn = groupRow.querySelector('.nativeColorBtn');
                const nativeColorInput = groupRow.querySelector('.nativeColorInput');

                nativeColorBtn.addEventListener('click', (e) => {
                    // Don't prevent default or stop propagation - iOS needs clean event flow
                    openColorPickerSafely(nativeColorInput, e);
                }, { passive: false });

                // Live preview while dragging
                nativeColorInput.addEventListener('input', () => {
                    const selected = nativeColorInput.value;
                    applyGroupColor(groupName, selected);
                    colorSwatchDisplay.style.background = selected;
                    if (groupData) groupData.color = selected;
                    groupRow.querySelectorAll('.swatch').forEach(s => s.classList.remove('active'));
                    requestRender();
                });

                // Save color only when picker is closed
                nativeColorInput.addEventListener('change', () => {
                    const selected = nativeColorInput.value;

                    // Save custom color and update ALL swatches
                    state.addCustomColor(selected);
                    refreshAllCustomColorSwatches();

                    const colorSwatches = groupRow.querySelector('.color-swatches');
                    addCustomColorSwatches(colorSwatches, (hexColor) => {
                        applyGroupColor(groupName, hexColor);
                        colorSwatchDisplay.style.background = hexColor;
                        if (groupData) groupData.color = hexColor;
                        window._markConfigurationChanged();
                    });

                    window._markConfigurationChanged();
                });

                // RAL Modal
                const ralColorBtn = groupRow.querySelector('.ralColorBtn');
                ralColorBtn.addEventListener('click', () => {
                    openRalModal((hexColor) => {
                        applyGroupColor(groupName, hexColor);
                        colorSwatchDisplay.style.background = hexColor;
                        if (groupData) groupData.color = hexColor;
                        groupRow.querySelectorAll('.swatch').forEach(s => s.classList.remove('active'));

                        // Save custom color and update ALL swatches
                        state.addCustomColor(hexColor);
                        refreshAllCustomColorSwatches();

                        const colorSwatches = groupRow.querySelector('.color-swatches');
                        addCustomColorSwatches(colorSwatches, (color) => {
                            applyGroupColor(groupName, color);
                            colorSwatchDisplay.style.background = color;
                            if (groupData) groupData.color = color;
                            window._markConfigurationChanged();
                        });

                        window._markConfigurationChanged();
                        requestRender();
                    });
                });

            }

            // Sonderfall: Wenn keine Multi-Side-Gruppen existieren, zeige default-Seiten als Gruppen
            if (!hasMultiSideGroups) {
                const defaultGroup = groups.get('default');
                if (defaultGroup && defaultGroup.sides && defaultGroup.sides.size > 0) {
                    // Zeige jede Seite der default-Gruppe als separate "Gruppe" - sortiert
                    const sideOrderDefault = ['basis', 'trailer', 'attika', 'geländer', 'terrasse', 'glas'];
                    const sortedDefaultSides = Array.from(defaultGroup.sides.entries()).sort(([nameA], [nameB]) => {
                        const normA = normalizeKey(nameA);
                        const normB = normalizeKey(nameB);

                        const aIsBasis = normA === 'basis';
                        const bIsBasis = normB === 'basis';
                        if (aIsBasis && !bIsBasis) return -1;
                        if (bIsBasis && !aIsBasis) return 1;

                        const indexA = sideOrderDefault.findIndex(s => normA.includes(s));
                        const indexB = sideOrderDefault.findIndex(s => normB.includes(s));
                        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                        if (indexA !== -1) return -1;
                        if (indexB !== -1) return 1;

                        return normA.localeCompare(normB);
                    });

                    for (const [sideName, sideData] of sortedDefaultSides) {
                        const firstMat = sideMats.get('default')?.get(sideName)?.values()?.next()?.value;
                        const origColor = firstMat?.userData?.originalColor;
                        const currentColor = sideData?.color || defaultGroup?.color || (origColor ? `#${origColor.getHexString()}` : '#FFFFFF');

                        // Prüfe ob Material "glas" im Namen hat (case insensitive)
                        const isGlass = firstMat?.name?.toLowerCase().includes('glas') || false;

                        const groupRow = document.createElement('div');
                        groupRow.className = 'group-color-row';
                        groupRow.dataset.group = 'default';
                        groupRow.dataset.side = sideName;

                        // Swatches: 8 Standardfarben (Camo-Textur auskommentiert)
                        const swatchesHTML = `
                            <div class="swatch" data-color="#F5F5F2" style="background:#F5F5F2; border: 1px solid #ddd;"></div>
                            <div class="swatch" data-color="#1C1C1C" style="background:#1C1C1C;"></div>
                            <div class="swatch" data-color="#E1E1E1" style="background:#E1E1E1;"></div>
                            <div class="swatch" data-color="#4A4A4A" style="background:#4A4A4A;"></div>
                            <div class="swatch" data-color="#C00000" style="background:#C00000;"></div>
                            <div class="swatch" data-color="#0D47A1" style="background:#0D47A1;"></div>
                            <div class="swatch" data-color="#FFD700" style="background:#FFD700;"></div>
                            <div class="swatch" data-color="#1B5E20" style="background:#1B5E20;"></div>
                        `;

                        groupRow.innerHTML = `
                            <div class="group-info">
                                <div class="color-swatch" style="background: ${currentColor};"></div>
                                <span class="group-label">${getLabelText(sideName)}</span>
                            </div>
                            <button type="button" class="edit-btn">
                                <i class="icon icon-edit"></i>
                            </button>
                            <button type="button" class="reset-btn">
                                <i class="icon icon-reload"></i>
                            </button>
                            <div class="color-editor">
                                <div class="color-swatches">
                                    ${swatchesHTML}
                                </div>
                                <div class="custom-color-buttons">
                                    <button type="button" class="nativeColorBtn">
                                        ${GLOBALS.tc.custom_color || 'Individuell'} <i class="icon icon-edit"></i>
                                    </button>
                                    <button type="button" class="ralColorBtn">
                                        ${GLOBALS.tc.ral_color || 'RAL-Farbe'} <i class="icon icon-edit"></i>
                                    </button>
                                    <input type="color" class="nativeColorInput">
                                </div>
                            </div>
                        `;

                        groupsList.appendChild(groupRow);

                        // Event Handlers für diese Seite (behandelt wie Gruppe)
                        const editBtn = groupRow.querySelector('.edit-btn');
                        const resetBtn = groupRow.querySelector('.reset-btn');
                        const colorEditor = groupRow.querySelector('.color-editor');
                        const colorSwatchDisplay = groupRow.querySelector('.color-swatch');

                        const toggleEditor = (forceOpen = false) => {
                            const isOpen = colorEditor.style.display === 'block';
                            if (forceOpen && isOpen) return;

                            const shouldOpen = forceOpen || !isOpen;

                            if (shouldOpen) {
                                document.querySelectorAll('.group-color-row, .side-color-row').forEach(row => {
                                    if (row !== groupRow) {
                                        const otherEditor = row.querySelector('.color-editor');
                                        const otherEditBtn = row.querySelector('.edit-btn');
                                        if (otherEditor && otherEditor.style.display === 'block') {
                                            otherEditor.style.display = 'none';
                                            if (otherEditBtn) otherEditBtn.innerHTML = '<i class="icon icon-edit"></i>';
                                            row.style.flexWrap = '';
                                        }
                                    }
                                });

                                colorEditor.style.display = 'block';
                                editBtn.innerHTML = '<i class="icon icon-check"></i>';
                                groupRow.style.flexWrap = 'wrap';
                                colorEditor.style.width = '100%';

                                // Load custom color swatches
                                const colorSwatches = colorEditor.querySelector('.color-swatches');
                                addCustomColorSwatches(colorSwatches, (hexColor) => {
                                    applySideColor('default', sideName, hexColor);
                                    colorSwatchDisplay.style.background = hexColor;
                                    if (sideData) sideData.color = hexColor;
                                    window._markConfigurationChanged();
                                });

                                // Markiere den aktuell ausgewählten Swatch als active
                                const activeColor = sideData?.color || defaultGroup?.color;
                                colorEditor.querySelectorAll('.swatch').forEach(s => s.classList.remove('active'));
                                if (activeColor === 'camo') {
                                    const camoSwatch = colorEditor.querySelector('.swatch[data-texture="camo"]');
                                    if (camoSwatch) camoSwatch.classList.add('active');
                                } else if (activeColor) {
                                    const activeSwatch = colorEditor.querySelector(`.swatch[data-color="${activeColor}"]`);
                                    if (activeSwatch) activeSwatch.classList.add('active');
                                }
                            } else {
                                colorEditor.style.display = 'none';
                                editBtn.innerHTML = '<i class="icon icon-edit"></i>';
                                groupRow.style.flexWrap = '';
                            }
                        };

                        if (colorSwatchDisplay) {
                            colorSwatchDisplay.setAttribute('role', 'button');
                            colorSwatchDisplay.setAttribute('tabindex', '0');
                            colorSwatchDisplay.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); toggleEditor(true); });
                            colorSwatchDisplay.addEventListener('keydown', (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    toggleEditor(true);
                                }
                            });
                        }

                        const groupLabel = groupRow.querySelector('.group-label');
                        if (groupLabel) {
                            groupLabel.style.cursor = 'pointer';
                            groupLabel.addEventListener('click', () => toggleEditor(true));
                        }

                        editBtn.addEventListener('click', () => toggleEditor());

                        resetBtn.addEventListener('click', () => {
                            resetSideColor('default', sideName);
                            const firstMat = sideMats.get('default')?.get(sideName)?.values()?.next()?.value;
                            const origColor = firstMat?.userData?.originalColor;
                            const resetColor = origColor ? `#${origColor.getHexString()}` : '#FFFFFF';
                            colorSwatchDisplay.style.background = resetColor;
                            if (sideData) sideData.color = null;
                            colorEditor.querySelectorAll('.swatch').forEach(s => s.classList.remove('active'));
                            window._markConfigurationChanged();
                        });

                        // Swatches
                        colorEditor.querySelectorAll('.swatch').forEach(swatch => {
                            swatch.addEventListener('click', () => {
                                const color = swatch.dataset.color;
                                const texture = swatch.dataset.texture;

                                if (texture === 'camo') {
                                    applySideTexture('default', sideName, texture);
                                    colorSwatchDisplay.style.background = 'url(/userdata/files/models/camo-texture.jpg)';
                                    colorSwatchDisplay.style.backgroundSize = 'cover';
                                    if (sideData) sideData.color = 'camo';
                                } else if (color) {
                                    applySideColor('default', sideName, color);
                                    colorSwatchDisplay.style.background = color;
                                    if (sideData) sideData.color = color;
                                }

                                colorEditor.querySelectorAll('.swatch').forEach(s => s.classList.remove('active'));
                                swatch.classList.add('active');
                                window._markConfigurationChanged();
                                requestRender();
                            });
                        });

                        // Native Color Picker
                        const nativeColorBtn = groupRow.querySelector('.nativeColorBtn');
                        const nativeColorInput = groupRow.querySelector('.nativeColorInput');

                        nativeColorBtn.addEventListener('click', (e) => {
                            // Don't prevent default or stop propagation - iOS needs clean event flow
                            openColorPickerSafely(nativeColorInput, e);
                        }, { passive: false });

                        nativeColorInput.addEventListener('input', (e) => {
                            const hexColor = e.target.value;
                            applySideColor('default', sideName, hexColor);
                            colorSwatchDisplay.style.background = hexColor;
                            if (sideData) sideData.color = hexColor;
                            colorEditor.querySelectorAll('.swatch').forEach(s => s.classList.remove('active'));
                            requestRender();
                        });

                        nativeColorInput.addEventListener('change', (e) => {
                            const hexColor = e.target.value;
                            state.addCustomColor(hexColor);
                            refreshAllCustomColorSwatches();

                            const colorSwatches = groupRow.querySelector('.color-swatches');
                            addCustomColorSwatches(colorSwatches, (color) => {
                                applySideColor('default', sideName, color);
                                colorSwatchDisplay.style.background = color;
                                if (sideData) sideData.color = color;
                                window._markConfigurationChanged();
                            });
                            window._markConfigurationChanged();
                        });

                        // RAL Modal
                        const ralColorBtn = groupRow.querySelector('.ralColorBtn');
                        ralColorBtn.addEventListener('click', () => {
                            openRalModal((hexColor) => {
                                applySideColor('default', sideName, hexColor);
                                colorSwatchDisplay.style.background = hexColor;
                                if (sideData) sideData.color = hexColor;
                                groupRow.querySelectorAll('.swatch').forEach(s => s.classList.remove('active'));

                                // Save custom color and update ALL swatches
                                state.addCustomColor(hexColor);
                                refreshAllCustomColorSwatches();

                                const colorSwatches = groupRow.querySelector('.color-swatches');
                                addCustomColorSwatches(colorSwatches, (color) => {
                                    applySideColor('default', sideName, color);
                                    colorSwatchDisplay.style.background = color;
                                    if (sideData) sideData.color = color;
                                    window._markConfigurationChanged();
                                });

                                window._markConfigurationChanged();
                                requestRender();
                            });
                        });
                    }
                }

                // Dropdown verstecken
                const inputGroup = colorSlide.querySelector('.inputGroup');
                if (inputGroup) inputGroup.style.display = 'none';
            }

            // === Seiten-Liste immer direkt erzeugen (Checkbox existiert ja nicht mehr) ===
            if (sidesList.children.length === 0) {
                // Sortiere Gruppen nach gewünschter Reihenfolge
                const groupOrderSidesList = ['basis', 'trailer', 'attika', 'geländer', 'terrasse', 'glas'];
                const sortedGroupsForSides = Array.from(groups.entries()).sort(([nameA], [nameB]) => {
                    const normA = normalizeKey(nameA);
                    const normB = normalizeKey(nameB);

                    const aIsBasis = normA === 'basis';
                    const bIsBasis = normB === 'basis';
                    if (aIsBasis && !bIsBasis) return -1;
                    if (bIsBasis && !aIsBasis) return 1;

                    const indexA = groupOrderSidesList.findIndex(g => normA.includes(g));
                    const indexB = groupOrderSidesList.findIndex(g => normB.includes(g));
                    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                    if (indexA !== -1) return -1;
                    if (indexB !== -1) return 1;

                    return normA.localeCompare(normB);
                });

                for (const [groupName, groupData] of sortedGroupsForSides) {
                    if (!groupData.sides) continue;

                    // Sortiere Seiten innerhalb der Gruppe
                    const sideOrderSidesList = ['basis', 'trailer', 'attika', 'geländer', 'terrasse', 'glas'];
                    const sortedSides = Array.from(groupData.sides.entries()).sort(([nameA], [nameB]) => {
                        const indexA = sideOrderSidesList.findIndex(s => normalizeKey(nameA).includes(s));
                        const indexB = sideOrderSidesList.findIndex(s => normalizeKey(nameB).includes(s));
                        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                        if (indexA !== -1) return -1;
                        if (indexB !== -1) return 1;
                        return nameA.localeCompare(nameB);
                    });

                    for (const [sideName, sideData] of sortedSides) {

                        const firstMat = sideMats.get(groupName)?.get(sideName)?.values()?.next()?.value;
                        const origColor = firstMat?.userData?.originalColor;
                        const currentColor =
                            sideData?.color ||
                            groupData?.color ||
                            (origColor ? `#${origColor.getHexString()}` : '#FFFFFF');

                        // Prüfe ob Material "glas" im Namen hat (case insensitive)
                        const isGlass = firstMat?.name?.toLowerCase().includes('glas') || false;

                        const sideRow = document.createElement('div');
                        sideRow.className = 'side-color-row';
                        sideRow.dataset.group = groupName;
                        sideRow.dataset.side = sideName;

                        // Swatches: 8 Standardfarben (Camo-Textur auskommentiert)
                        const swatchesHTML = `
                            <div class="swatch" data-color="#F5F5F2" style="background:#F5F5F2; border: 1px solid #ddd;"></div>
                            <div class="swatch" data-color="#1C1C1C" style="background:#1C1C1C;"></div>
                            <div class="swatch" data-color="#E1E1E1" style="background:#E1E1E1;"></div>
                            <div class="swatch" data-color="#4A4A4A" style="background:#4A4A4A;"></div>
                            <div class="swatch" data-color="#C00000" style="background:#C00000;"></div>
                            <div class="swatch" data-color="#0D47A1" style="background:#0D47A1;"></div>
                            <div class="swatch" data-color="#FFD700" style="background:#FFD700;"></div>
                            <div class="swatch" data-color="#1B5E20" style="background:#1B5E20;"></div>
                        `;

                                sideRow.innerHTML = `
                <div class="side-info">
                    <div class="color-swatch" style="background:${currentColor};"></div>
                    <span class="side-label">${getLabelText(groupName, sideName)}</span>
                </div>
                <button type="button" class="edit-btn"><i class="icon icon-edit"></i></button>
                <button type="button" class="reset-btn"><i class="icon icon-reload"></i></button>
                <div class="color-editor">
                    <div class="color-swatches">
                        ${swatchesHTML}
                    </div>
                    <div class="custom-color-buttons">
                        <button type="button" class="nativeColorBtn">
                            ${GLOBALS.tc.custom_color || 'Individuell'} <i class="icon icon-edit"></i>
                        </button>
                        <button type="button" class="ralColorBtn">
                            ${GLOBALS.tc.ral_color || 'RAL-Farbe'} <i class="icon icon-edit"></i>
                        </button>

                        <!-- versteckter color input -->
                        <input type="color" class="nativeColorInput">
                    </div>
                </div>
            `;



                        sideRow.style.display = 'none';

                        sidesList.appendChild(sideRow);

                        // === EVENTS FÜR SEITEN (müssen INNERHALB der Schleife sein!) ===
                        const editBtn = sideRow.querySelector('.edit-btn');
                        const colorEditor = sideRow.querySelector('.color-editor');
                        const colorSwatchDisplay = sideRow.querySelector('.color-swatch');

                        const toggleSideEditor = (forceOpen = false) => {
                            const isOpen = colorEditor.style.display === 'block';
                            if (forceOpen && isOpen) return;

                            const shouldOpen = forceOpen || !isOpen;

                            if (shouldOpen) {
                                document.querySelectorAll('.group-color-row, .side-color-row').forEach(row => {
                                    if (row !== sideRow) {
                                        const otherEditor = row.querySelector('.color-editor');
                                        const otherEditBtn = row.querySelector('.edit-btn');
                                        if (otherEditor && otherEditor.style.display === 'block') {
                                            otherEditor.style.display = 'none';
                                            if (otherEditBtn) otherEditBtn.innerHTML = '<i class="icon icon-edit"></i>';
                                            row.style.flexWrap = '';
                                        }
                                    }
                                });
                            }

                            colorEditor.style.display = shouldOpen ? 'block' : 'none';

                            // Icon wechseln zwischen edit und check
                            if (shouldOpen) {
                                editBtn.innerHTML = '<i class="icon icon-check"></i>';
                            } else {
                                editBtn.innerHTML = '<i class="icon icon-edit"></i>';
                            }

                            // Wenn Editor geöffnet wird, markiere den aktuell ausgewählten Swatch als active
                            if (shouldOpen && groupData?.sides?.get(sideName)) {
                                const sideData = groupData.sides.get(sideName);
                                const currentColor = sideData.color || groupData.color;

                                // Update color swatch display box
                                if (currentColor === 'camo') {
                                    colorSwatchDisplay.style.backgroundImage = 'url(/userdata/files/models/camo-texture.jpg)';
                                    colorSwatchDisplay.style.backgroundSize = 'cover';
                                    colorSwatchDisplay.style.backgroundColor = '';
                                } else if (currentColor) {
                                    colorSwatchDisplay.style.background = currentColor;
                                    colorSwatchDisplay.style.backgroundImage = '';
                                }

                                colorEditor.querySelectorAll('.swatch').forEach(s => s.classList.remove('active'));

                                if (currentColor === 'camo') {
                                    const camoSwatch = colorEditor.querySelector('.swatch[data-texture="camo"]');
                                    if (camoSwatch) camoSwatch.classList.add('active');
                                } else if (currentColor) {
                                    const activeSwatch = colorEditor.querySelector(`.swatch[data-color="${currentColor}"]`);
                                    if (activeSwatch) activeSwatch.classList.add('active');
                                }
                            }
                        };

                        editBtn.addEventListener('click', () => toggleSideEditor());
                        colorSwatchDisplay.addEventListener('click', () => toggleSideEditor(true));

                        const sideLabel = sideRow.querySelector('.side-label');
                        if (sideLabel) {
                            sideLabel.style.cursor = 'pointer';
                            sideLabel.addEventListener('click', () => toggleSideEditor(true));
                        }

                        const resetBtn = sideRow.querySelector('.reset-btn');
                        resetBtn.addEventListener('click', () => {
                            resetSideColor(groupName, sideName);
                            // Entferne active Klasse von allen Swatches
                            colorEditor.querySelectorAll('.swatch').forEach(s => s.classList.remove('active'));

                            // Setze die Color-Swatch auf ursprüngliche Farbe zurück
                            const swatchBox = sideRow.querySelector('.color-swatch');
                            const sideData = groups.get(groupName)?.sides?.get(sideName);
                            const origColor = sideData?.mats?.values().next().value?.userData?.originalColor;
                            const displayColor = origColor ? `#${origColor.getHexString()}` : '#FFFFFF';
                            swatchBox.style.background = displayColor;
                            swatchBox.style.backgroundImage = '';
                        });

// Swatches
                        colorEditor.querySelectorAll('.swatch').forEach(swatch => {
                            swatch.addEventListener('click', () => {
                                colorEditor.querySelectorAll('.swatch').forEach(s => s.classList.remove('active'));
                                swatch.classList.add('active');

                                const color = swatch.dataset.color;
                                const texture = swatch.dataset.texture;

                                if (texture === 'camo') {
                                    applySideCamoTexture(groupName, sideName);
                                    const swatchBox = sideRow.querySelector('.color-swatch');
                                    swatchBox.style.backgroundImage = 'url(/userdata/files/models/camo-texture.jpg)';
                                    swatchBox.style.backgroundSize = 'cover';
                                    swatchBox.style.backgroundColor = '';
                                    window._markConfigurationChanged();
                                } else {
                                    applySideColor(groupName, sideName, color);
                                    const swatchBox = sideRow.querySelector('.color-swatch');
                                    swatchBox.style.background = color;
                                    swatchBox.style.backgroundImage = '';
                                    window._markConfigurationChanged();
                                }

                                requestRender();
                            });
                        });

// Native Color Picker
                        const nativeColorBtn = sideRow.querySelector('.nativeColorBtn');
                        const nativeColorInput = sideRow.querySelector('.nativeColorInput');

                        nativeColorBtn.addEventListener('click', (e) => {
                            // Don't prevent default or stop propagation - iOS needs clean event flow
                            openColorPickerSafely(nativeColorInput, e);
                        }, { passive: false });

                        nativeColorInput.addEventListener('input', () => {
                            const selected = nativeColorInput.value;
                            applySideColor(groupName, sideName, selected);

                            const swatchBox = sideRow.querySelector('.color-swatch');
                            swatchBox.style.background = selected;
                            swatchBox.style.backgroundImage = '';

                            if (groupData?.sides?.get(sideName)) {
                                groupData.sides.get(sideName).color = selected;
                            }

                            colorEditor.querySelectorAll('.swatch').forEach(s => s.classList.remove('active'));

                            window._markConfigurationChanged();
                            requestRender();
                        });

                        nativeColorInput.addEventListener('change', () => {
                            const selected = nativeColorInput.value;
                            state.addCustomColor(selected);
                            refreshAllCustomColorSwatches();
                        });

                        const ralColorBtn = sideRow.querySelector('.ralColorBtn');
                        ralColorBtn.addEventListener('click', () => {
                            openRalModal((hexColor) => {
                                applySideColor(groupName, sideName, hexColor);

                                const swatchBox = sideRow.querySelector('.color-swatch');
                                swatchBox.style.background = hexColor;
                                swatchBox.style.backgroundImage = '';

                                if (groupData?.sides?.get(sideName)) {
                                    groupData.sides.get(sideName).color = hexColor;
                                }

                                colorEditor.querySelectorAll('.swatch').forEach(s => s.classList.remove('active'));

                                state.addCustomColor(hexColor);
                                refreshAllCustomColorSwatches();

                                window._markConfigurationChanged();
                                requestRender();
                            });
                        });

                        // Add custom color swatches für diese Seite
                        const colorSwatches = sideRow.querySelector('.color-swatches');
                        addCustomColorSwatches(colorSwatches, (hexColor) => {
                            applySideColor(groupName, sideName, hexColor);
                            const swatchBox = sideRow.querySelector('.color-swatch');
                            swatchBox.style.background = hexColor;
                            swatchBox.style.backgroundImage = '';
                            if (groupData?.sides?.get(sideName)) {
                                groupData.sides.get(sideName).color = hexColor;
                            }
                            window._markConfigurationChanged();
                            requestRender();
                        });

                    }
                }
            }

            switcher.addEventListener('change', (e) => {
                const val = e.target.value;

                if (val === 'groups') {
                    // Gruppen anzeigen
                    groupsList.style.display = 'block';
                    sidesList.style.display = 'none';
                    return;
                }

                // Einzel-Seite anzeigen
                groupsList.style.display = 'none';
                sidesList.style.display = 'block';

                const [g, s] = val.split(':');

                // Alle Seiten verstecken
                sidesList.querySelectorAll('.side-color-row')
                    .forEach(r => r.style.display = 'none');

                // Nur die richtige Seite anzeigen
                const row = sidesList.querySelector(`.side-color-row[data-group="${g}"][data-side="${s}"]`);
                if (row) row.style.display = 'flex';

                // Kamera zur Seite bewegen (ohne Zoom zu ändern)
                animateCameraToSide(g, s);
            });

            panelContainer.appendChild(colorSlide);
        }

        // 3. Grafikupload Slide - Wiederverwendung der existierenden Slide falls vorhanden
        let graphicsSlide = existingGraphicsSlide;

        if (!graphicsSlide) {
            graphicsSlide = document.createElement('div');
            graphicsSlide.className = 'cfgSlideItem';
            graphicsSlide.dataset.type = 'graphics-upload';
            graphicsSlide.innerHTML = `
                <div class="cfgSlideItem__headline">${GLOBALS.tc.upload_graphic|| 'Grafikupload'}</div>
                <div class="cfgSlideItem__content" style="display:none;">
                    <div class="graphics-list"></div>
                    <div class="graphics-upload-container"></div>
                </div>
            `;
        }

        const graphicsList = graphicsSlide.querySelector('.graphics-list');
        const graphicsUploadContainer = graphicsSlide.querySelector('.graphics-upload-container');

        const MAX_GRAPHICS = 8;

        // Funktion zum Erstellen eines Upload-Felds
        function createUploadField(index) {
            const uploadField = document.createElement('div');
            uploadField.className = 'graphic-upload-field';
            uploadField.dataset.index = index;

            // Auf Mobile kürzerer Text
            const isMobile = window.innerWidth <= 767;
            const uploadText = isMobile
                ? (GLOBALS.tc.upload_file_mobile || 'Datei hochladen')
                : (GLOBALS.tc.upload_file || 'Datei hochladen');

            uploadField.innerHTML = `
                <div class="inputGroup upload-section">
                    <div class="fileBody">
                        <input name="input_data" type="file" accept="image/*" class="graphicInput" id="graphicInput-${index}">
                        <i class="icon icon-upload"></i>
                        <span>${uploadText}</span>
                    </div>
                </div>
            `;

            const input = uploadField.querySelector('.graphicInput');
            const fileBody = uploadField.querySelector('.fileBody');
            const labelSpan = fileBody.querySelector('span');
            const icon = fileBody.querySelector('i');

            // Click-Handler für fileBody um Input zu triggern
            fileBody.addEventListener('click', () => {
                input.click();
            });

            input.addEventListener('change', (e) => {
                const file = e.target.files?.[0];
                if (!file) return;

                const reader = new FileReader();
                reader.onload = (event) => {
                    const imageUrl = event.target.result;

                    // Alle anderen Grafiken aus Bearbeitungsmodus nehmen
                    uploadedGraphics.forEach(g => {
                        g.isEditing = false;
                    });
                    const panelContainer = document.querySelector('.configuratorPanel');
                    panelContainer?.querySelectorAll('.graphic-row').forEach(row => {
                        row.querySelector('.graphic-controls').style.display = 'none';
                        row.querySelector('.edit-graphic-btn').innerHTML = '<i class="icon icon-edit"></i>';
                        row.style.flexWrap = '';
                    });

                    // Grafik zum Array hinzufügen - direkt im Bearbeitungsmodus
                    const graphic = {
                        id: `graphic-${Date.now()}-${index}`,
                        file: file,
                        filename: file.name,
                        imageUrl: imageUrl,
                        index: index,
                        rotation: 0,
                        scale: 1,
                        isEditing: true  // Direkt im Bearbeitungsmodus
                    };

                    uploadedGraphics.push(graphic);
                    setCurrentEditingGraphic(graphic);

                    window._markConfigurationChanged();

                    // Upload-Feld entfernen
                    uploadField.remove();

                    // Grafik zur Liste hinzufügen
                    addGraphicToList(graphic);

                    // Grafik direkt auf Modell anwenden
                    applyGraphicToModel(graphic);

                    // Nächstes Upload-Feld erstellen, wenn noch nicht max erreicht
                    if (uploadedGraphics.length < MAX_GRAPHICS) {
                        graphicsUploadContainer.appendChild(createUploadField(uploadedGraphics.length));
                    }
                };
                reader.readAsDataURL(file);
            });

            return uploadField;
        }

        // Funktion zum Hinzufügen einer Grafik zur Liste
        function addGraphicToList(graphic) {
            const graphicRow = document.createElement('div');
            graphicRow.className = 'graphic-row';
            graphicRow.dataset.graphicId = graphic.id;

            graphicRow.innerHTML = `
                <div class="graphic-info">
                    <div class="graphic-preview" style="background-image: url('${graphic.imageUrl}');"></div>
                    <span class="graphic-name">${graphic.filename}</span>
                </div>
                <div class="graphic-buttons">
                    <button type="button" class="edit-graphic-btn">
                        <i class="icon icon-edit"></i>
                    </button>
                    <button type="button" class="delete-graphic-btn">
                        <i class="icon icon-trash"></i>
                    </button>
                </div>
                <div class="graphic-controls" style="display: none; width: 100%; padding: 10px 0;">
                    <div class="graphic-placement-hint" style="display: block; padding: 10px; background: #f5f5f5; border-radius: 4px; margin-bottom: 10px;">
                        <b>${GLOBALS.tc.place_graphic || 'Grafik platzieren'}:</b><br>
                        <span style="font-size: 0.9em;">${GLOBALS.tc.click_to_place || 'Klicken Sie auf das Modell, um die Grafik zu platzieren'}</span>
                    </div>
                    <div class="graphic-sliders" style="display: none;">
                        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 5px;">
                            <label>${GLOBALS.tc.scale || 'Skalierung'}:</label>
                            <input type="number" min="0.1" max="10" value="1" step="0.01" class="graphicScaleInput" style="width: 80px; padding: 4px; border: 1px solid #ccc; border-radius: 4px; text-align: right;">
                        </div>
                        <input type="range" min="0.1" max="10" value="1" step="0.01" class="graphicScaleSlider" style="margin-bottom: 15px;">
                        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 5px;">
                            <label>${GLOBALS.tc.rotation || 'Rotation'}:</label>
                            <input type="number" min="-180" max="180" value="0" step="1" class="graphicRotateInput" style="width: 80px; padding: 4px; border: 1px solid #ccc; border-radius: 4px; text-align: right;">
                        </div>
                        <input type="range" min="-180" max="180" value="0" step="1" class="graphicRotateSlider">
                    </div>
                </div>
            `;

            graphicsList.appendChild(graphicRow);

            // Event Handlers
            const editBtn = graphicRow.querySelector('.edit-graphic-btn');
            const deleteBtn = graphicRow.querySelector('.delete-graphic-btn');
            const graphicControls = graphicRow.querySelector('.graphic-controls');
            const placementHint = graphicRow.querySelector('.graphic-placement-hint');
            const graphicSliders = graphicRow.querySelector('.graphic-sliders');
            const rotSlider = graphicRow.querySelector('.graphicRotateSlider');
            const scaleSlider = graphicRow.querySelector('.graphicScaleSlider');
            const rotInput = graphicRow.querySelector('.graphicRotateInput');
            const scaleInput = graphicRow.querySelector('.graphicScaleInput');

            // Funktion um Hint/Sliders basierend auf Platzierung zu aktualisieren
            const updateGraphicUI = () => {
                const isPlaced = graphicLayerMap.get(graphic.id) !== undefined;
                if (placementHint) placementHint.style.display = isPlaced ? 'none' : 'block';
                if (graphicSliders) graphicSliders.style.display = isPlaced ? 'block' : 'none';
            };

            // Funktion global speichern für Updates von außen
            if (!window._graphicUIUpdaters) window._graphicUIUpdaters = new Map();
            window._graphicUIUpdaters.set(graphic.id, updateGraphicUI);

            // Wenn die Grafik bereits im Bearbeitungsmodus ist, Controls anzeigen
            if (graphic.isEditing) {
                graphicControls.style.display = 'block';
                editBtn.innerHTML = '<i class="icon icon-check"></i>';
                graphicRow.style.flexWrap = 'wrap';
                updateGraphicUI();
            }

            // Bearbeiten-Button
            editBtn.addEventListener('click', () => {
                if (graphic.isEditing) {
                    graphic.isEditing = false;
                    setCurrentEditingGraphic(null);
                    graphicControls.style.display = 'none';
                    editBtn.innerHTML = '<i class="icon icon-edit"></i>';
                    graphicRow.style.flexWrap = '';

                    lockedGroupDecals.set(graphic.id, true);

                    // Remove buttons from overlay
                    const buttonsToRemove = viewportButtonsMap.get(graphic.id);
                    if (buttonsToRemove) {
                        buttonsToRemove.confirmBtn.remove();
                        buttonsToRemove.deleteBtn.remove();
                        viewportButtonsMap.delete(graphic.id);
                    }

                    // Remove 3D frame from scene (also on mobile where buttons don't exist)
                    const frameKey = `decal-frame-${graphic.id}`;
                    const frame3D = scene.getObjectByName(frameKey);
                    if (frame3D) {
                        scene.remove(frame3D);
                        frame3D.geometry.dispose();
                        frame3D.material.dispose();
                    }

                    updateAllMaterialsWithGraphicLayers();
                    requestRender();
                } else {
                    const panelContainer = document.querySelector('.configuratorPanel');
                    panelContainer?.querySelectorAll('.graphic-row').forEach(row => {
                        const rowGraphicId = row.dataset.graphicId;
                        const rowGraphic = uploadedGraphics.find(g => g.id === rowGraphicId);
                        if (rowGraphic && rowGraphic.isEditing) {
                            rowGraphic.isEditing = false;
                            row.querySelector('.graphic-controls').style.display = 'none';
                            row.querySelector('.edit-graphic-btn').innerHTML = '<i class="icon icon-edit"></i>';
                            row.style.flexWrap = '';

                            lockedGroupDecals.set(rowGraphic.id, true);

                            // Remove buttons and frame from previously edited graphic
                            const buttonsToRemove = viewportButtonsMap.get(rowGraphic.id);
                            if (buttonsToRemove) {
                                buttonsToRemove.confirmBtn.remove();
                                buttonsToRemove.deleteBtn.remove();
                                viewportButtonsMap.delete(rowGraphic.id);
                            }

                            // Remove 3D frame from scene (also on mobile where buttons don't exist)
                            const frameKey = `decal-frame-${rowGraphic.id}`;
                            const frame3D = scene.getObjectByName(frameKey);
                            if (frame3D) {
                                scene.remove(frame3D);
                                frame3D.geometry.dispose();
                                frame3D.material.dispose();
                            }
                        }
                    });

                    graphic.isEditing = true;
                    setCurrentEditingGraphic(graphic);
                    graphicControls.style.display = 'block';
                    editBtn.innerHTML = '<i class="icon icon-check"></i>';
                    graphicRow.style.flexWrap = 'wrap';

                    updateGraphicUI();

                    if (pendingGroupTexture && pendingGroupTexture.graphicId === graphic.id) {
                        lockedGroupDecals.set(graphic.id, false);
                    } else {
                        const layerIndex = graphicLayerMap.get(graphic.id);
                        if (layerIndex !== undefined) {
                            lockedGroupDecals.set(graphic.id, false);
                            setActiveGroup(graphic.id);
                            setActiveSide(null);
                            setActiveSideKey(null);
                        } else {
                            // Grafik auf Modell laden
                            applyGraphicToModel(graphic);
                        }
                    }

                    updateAllMaterialsWithGraphicLayers();
                    requestRender();
                }
            });

            // Löschen-Button
            deleteBtn.addEventListener('click', () => {
                // Grafik aus Array entfernen
                const graphicIndex = uploadedGraphics.findIndex(g => g.id === graphic.id);
                if (graphicIndex > -1) {
                    uploadedGraphics.splice(graphicIndex, 1);
                }

                // Grafik vom Modell entfernen (removeGraphicFromModel logic)
                if (pendingGroupTexture && pendingGroupTexture.graphicId === graphic.id) {
                    setPendingGroupTexture(null);
                }

                if (activeGroup === graphic.id) {
                    setActiveGroup(null);
                }

                // Remove from layer system
                const layerIndex = graphicLayerMap.get(graphic.id);
                if (layerIndex !== undefined) {
                    graphicLayers[layerIndex] = null;
                    graphicLayerMap.delete(graphic.id);
                }

                updateAllMaterialsWithGraphicLayers();
                window._markConfigurationChanged();

                groupDecalSettings.delete(graphic.id);
                decalSettings.delete(graphic.id);
                lockedGroupDecals.delete(graphic.id);

                // Row entfernen
                graphicRow.remove();

                // Viewport-Buttons und 3D-Rahmen entfernen
                const buttonsToRemove = viewportButtonsMap.get(graphic.id);
                if (buttonsToRemove) {
                    buttonsToRemove.confirmBtn.remove();
                    buttonsToRemove.deleteBtn.remove();
                    viewportButtonsMap.delete(graphic.id);
                }

                // 3D-Rahmen aus der Szene entfernen
                const frameKey = `decal-frame-${graphic.id}`;
                const frame3D = scene.getObjectByName(frameKey);
                if (frame3D) {
                    scene.remove(frame3D);
                    if (frame3D.geometry) frame3D.geometry.dispose();
                    if (frame3D.material) frame3D.material.dispose();
                }

                // Upload-Feld wieder hinzufügen wenn weniger als MAX_GRAPHICS
                if (uploadedGraphics.length < MAX_GRAPHICS) {
                    // Prüfen ob schon ein Upload-Feld existiert
                    if (!graphicsUploadContainer.querySelector('.graphic-upload-field')) {
                        graphicsUploadContainer.appendChild(createUploadField(uploadedGraphics.length));
                    }
                }

                if (currentEditingGraphic?.id === graphic.id) {
                    setCurrentEditingGraphic(null);
                }

                requestRender();
            });

            // Rotation Slider
            let rebuildPending = null;
            rotSlider.oninput = (e) => {
                const deg = parseFloat(e.target.value);
                rotInput.value = deg.toFixed(0);
                graphic.rotation = deg;
                cancelAnimationFrame(rebuildPending);
                rebuildPending = requestAnimationFrame(() => rebuildGraphicDecal(graphic));
            };

            // Rotation Input
            rotInput.oninput = (e) => {
                let deg = parseFloat(e.target.value);
                // Clamp value to slider range
                deg = Math.max(-180, Math.min(180, deg));
                rotSlider.value = deg;
                graphic.rotation = deg;
                cancelAnimationFrame(rebuildPending);
                rebuildPending = requestAnimationFrame(() => rebuildGraphicDecal(graphic));
            };

            // Scale Slider
            scaleSlider.oninput = (e) => {
                const val = parseFloat(e.target.value);
                scaleInput.value = val.toFixed(2);
                graphic.scale = val;
                cancelAnimationFrame(rebuildPending);
                rebuildPending = requestAnimationFrame(() => rebuildGraphicDecal(graphic));
            };

            // Scale Input
            scaleInput.oninput = (e) => {
                let val = parseFloat(e.target.value);
                // Clamp value to slider range
                val = Math.max(0.1, Math.min(4, val));
                scaleSlider.value = val;
                graphic.scale = val;
                cancelAnimationFrame(rebuildPending);
                rebuildPending = requestAnimationFrame(() => rebuildGraphicDecal(graphic));
            };

            // Initialize input values from graphic properties
            if (graphic.rotation !== undefined) {
                rotSlider.value = graphic.rotation;
                rotInput.value = graphic.rotation.toFixed(0);
            }
            if (graphic.scale !== undefined) {
                scaleSlider.value = graphic.scale;
                scaleInput.value = graphic.scale.toFixed(2);
            }
        }

        // Funktion zum Anwenden einer Grafik auf das Modell
        function applyGraphicToModel(graphic) {
            // Ähnlich wie onGroupDecalUpload, aber für alle Configurator-Materialien
            const texture = textureLoader.load(graphic.imageUrl, (tex) => {
                tex.colorSpace = THREE.SRGBColorSpace;
                tex.needsUpdate = true;

                setPendingGroupTexture({
                    texture: tex,
                    graphicId: graphic.id,
                    rotation: graphic.rotation,
                    scale: graphic.scale
                });

                // activeGroup auf die Grafik-ID setzen
                setActiveGroup(graphic.id);
                setActiveSide(null);
                setActiveSideKey(null);

                // Grafik initial freigeben (umplatzierbar) wenn im Bearbeitungsmodus
                lockedGroupDecals.set(graphic.id, !graphic.isEditing);

                updateMarkerUI();
                // showDecalHint() entfernt - Hint wird jetzt inline bei den Controls angezeigt

                if (window._closeMobilePanel && typeof window._closeMobilePanel === 'function') {
                    window._closeMobilePanel();
                }
            });
        }

        // Funktion zum Entfernen einer Grafik vom Modell
        function removeGraphicFromModel(graphic) {
            if (pendingGroupTexture && pendingGroupTexture.graphicId === graphic.id) {
                setPendingGroupTexture(null);
            }

            if (activeGroup === graphic.id) {
                setActiveGroup(null);
            }

            // Remove from layer system
            const layerIndex = graphicLayerMap.get(graphic.id);
            if (layerIndex !== undefined) {
                graphicLayers[layerIndex] = null;
                graphicLayerMap.delete(graphic.id);
            }

            // Update all materials to reflect removed layer
            updateAllMaterialsWithGraphicLayers();

            groupDecalSettings.delete(graphic.id);
            decalSettings.delete(graphic.id);
            lockedGroupDecals.delete(graphic.id);

            requestRender();
        }

        // Funktion zum Rebuild eines Grafik-Decals
        function rebuildGraphicDecal(graphic) {
            const layerIndex = graphicLayerMap.get(graphic.id);
            if (layerIndex === undefined) return;

            const img = graphic.texture?.image || graphicLayers[layerIndex]?.texture?.image;
            if (!img) return;

            const aspectRatio = img.width / img.height;
            const baseSizeUV = 0.5;

            // Update the layer with new rotation/scale
            if (graphicLayers[layerIndex]) {
                graphicLayers[layerIndex].sizeUV = new THREE.Vector2(
                    baseSizeUV * aspectRatio * graphic.scale,
                    baseSizeUV * graphic.scale
                );
                graphicLayers[layerIndex].rotation = THREE.MathUtils.degToRad(graphic.rotation);
            }

            // Update groupDecalSettings for onPointerDown
            groupDecalSettings.set(graphic.id, {
                rotation: graphic.rotation,
                scale: graphic.scale,
                file: graphic.file,
                filename: graphic.filename
            });

            // Invalidate border points cache for this graphic (rotation/scale changed)
            invalidateBorderPointsCache(graphic.id);

            updateAllMaterialsWithGraphicLayers();
            requestRender();
        }

        // Erstes Upload-Feld erstellen (nur bei neuer Slide)
        if (!existingGraphicsSlide) {
            graphicsUploadContainer.appendChild(createUploadField(0));
        }

        panelContainer.appendChild(graphicsSlide);

        // Make addGraphicToList available for configuration loading
        window._addGraphicToList = addGraphicToList;

        // Sperre ALLE Grafiken, wenn der Grafiken-Slide wirklich geschlossen wurde
        graphicsSlide.addEventListener('cfgslide:closed', () => {

            uploadedGraphics.forEach(graphic => {
                graphic.isEditing = false;
                lockedGroupDecals.set(graphic.id, true);
            });

            setCurrentEditingGraphic(null);
            setActiveGroup(null);

            // UI zurücksetzen
            const panelContainer = document.querySelector('.configuratorPanel');
            panelContainer?.querySelectorAll('.graphic-row').forEach(row => {
                row.querySelector('.graphic-controls').style.display = 'none';
                row.querySelector('.edit-graphic-btn').innerHTML = '<i class="icon icon-edit"></i>';
                row.style.flexWrap = '';
            });

            // Overlay-Icons entfernen
            const overlayContainer = containerEl.querySelector('.decal-overlay-container');
            if (overlayContainer) overlayContainer.innerHTML = '';

            updateAllMaterialsWithGraphicLayers();
            requestRender();
        });


        initSizeSelector(window._reloadModel, true);

        setTimeout(() => {
            initCfgSlideItems();
            enhanceCfgSlideSync();
        }, 50);

        setTimeout(() => {
            initCfgSlideItems();
            enhanceCfgSlideSync();
        }, 200);

        setTimeout(() => {
            initCfgSlideItems();
            enhanceCfgSlideSync();
        }, 500);

        // Mobile Tab-Layout initialisieren
        initMobileTabs();

    // === Local Helper Functions for UI ===
    // These are local implementations used within buildUIFromMarkers
    // They use the imported textureLoader from sceneManager

    function applyGroupColor(groupName, color) {
        const groups = window._configGroups;
        if (!groups || !groups.has(groupName)) {
            return;
        }
        const groupData = groups.get(groupName);
        if (!groupData || !groupData.sides) {
            return;
        }
        groupData.color = color;

        for (const [sideName, sideData] of groupData.sides.entries()) {
            if (!sideData.color) {
                const matsArray = Array.from(sideData.mats);
                matsArray.forEach(m => {
                    if (m.userData.hasTextureOverride) {
                        return;
                    }

                    // WICHTIG: Entferne IMMER die Map bei Farbanwendung (auch bei Decal-Materialien)
                    if (m.map) {
                        m.map.dispose();
                        m.map = null;
                    }

                    // Speichere Original-Materialwerte beim ersten Mal
                    if (m.userData.originalMetalness === undefined) {
                        m.userData.originalMetalness = m.metalness;
                        m.userData.originalRoughness = m.roughness;
                    }

                    m.color.set(color);
                    if (m.emissive) {
                        m.emissive.set(0x000000);
                    }
                    // Behalte Original-Materialwerte
                    m.metalness = m.userData.originalMetalness;
                    m.roughness = m.userData.originalRoughness;
                    m.needsUpdate = true;
                });
            }
        }

        for (const [sideName, sideData] of groupData.sides.entries()) {
            if (!sideData.color) {
                const meshesForSide = sideMeshes.get(groupName)?.get(sideName);
                if (meshesForSide) {
                    meshesForSide.forEach(mesh => {
                        getMaterialsArray(mesh.material).forEach(mat => {
                            if (mat && mat.color && !mat.userData.hasTextureOverride) {
                                // WICHTIG: Entferne IMMER die Map bei Farbanwendung
                                if (mat.map) {
                                    mat.map.dispose();
                                    mat.map = null;
                                }

                                // Speichere Original-Materialwerte beim ersten Mal
                                if (mat.userData.originalMetalness === undefined) {
                                    mat.userData.originalMetalness = mat.metalness;
                                    mat.userData.originalRoughness = mat.roughness;
                                }

                                mat.color.set(color);
                                if (mat.emissive) {
                                    mat.emissive.set(0x000000);
                                }
                                // Behalte Original-Materialwerte
                                mat.metalness = mat.userData.originalMetalness;
                                mat.roughness = mat.userData.originalRoughness;
                                mat.needsUpdate = true;
                            }
                        });

                        if (Array.isArray(mesh.material)) {
                            mesh.material = [...mesh.material];
                        }
                    });
                }
            }
        }

        // Update side-color-row swatches for sides that inherit group color
        if (groupData.sides) {
            for (const [sideName, sideData] of groupData.sides.entries()) {
                if (!sideData.color) {
                    const sideColorRow = panelContainer?.querySelector(`.side-color-row[data-group="${groupName}"][data-side="${sideName}"]`);
                    const colorSwatchDisplay = sideColorRow?.querySelector('.color-swatch');

                    if (colorSwatchDisplay) {
                        colorSwatchDisplay.style.background = color;
                        colorSwatchDisplay.style.backgroundImage = '';
                    }
                }
            }
        }

        requestRender();
    }

    function getCamoTexture(callback) {
        if (state.cachedCamoTexture) {
            state.cachedCamoTexture.repeat.set(0.66, 0.66);
            callback(state.cachedCamoTexture);
            return;
        }

        const texturePath = '/userdata/files/models/camo-texture.jpg';
        textureLoader.load(texturePath, (camoTexture) => {
            camoTexture.wrapS = camoTexture.wrapT = THREE.RepeatWrapping;
            camoTexture.repeat.set(0.66, 0.66);
            camoTexture.colorSpace = THREE.SRGBColorSpace;
            setCachedCamoTexture(camoTexture);
            callback(camoTexture);
        });
    }

    function applyGroupCamoTexture(groupName) {
        getCamoTexture((camoTexture) => {
            const groups = window._configGroups;
            if (!groups || !groups.has(groupName)) {
                return;
            }
            const groupData = groups.get(groupName);
            if (!groupData || !groupData.sides) {
                return;
            }
            groupData.color = 'camo';

            for (const [sideName, sideData] of groupData.sides.entries()) {
                if (!sideData.color) {
                    const matsArray = Array.from(sideData.mats);
                    matsArray.forEach(m => {
                        if (m.userData.hasTextureOverride) return;

                        // Speichere Original-Materialwerte beim ersten Mal
                        if (m.userData.originalMetalness === undefined) {
                            m.userData.originalMetalness = m.metalness;
                            m.userData.originalRoughness = m.roughness;
                        }

                        m.map = camoTexture;
                        m.color.set(0xffffff);
                        if (m.emissive) {
                            m.emissive.set(0x000000);
                        }
                        // Behalte Original-Materialwerte
                        m.metalness = m.userData.originalMetalness;
                        m.roughness = m.userData.originalRoughness;
                        m.needsUpdate = true;
                    });
                }
            }

            for (const [sideName, sideData] of groupData.sides.entries()) {
                if (!sideData.color) {
                    const meshesForSide = sideMeshes.get(groupName)?.get(sideName);
                    if (meshesForSide) {
                        meshesForSide.forEach(mesh => {
                            getMaterialsArray(mesh.material).forEach(mat => {
                                if (mat && !mat.userData.hasTextureOverride) {
                                    // Speichere Original-Materialwerte beim ersten Mal
                                    if (mat.userData.originalMetalness === undefined) {
                                        mat.userData.originalMetalness = mat.metalness;
                                        mat.userData.originalRoughness = mat.roughness;
                                    }

                                    mat.map = camoTexture;
                                    mat.color.set(0xffffff);
                                    if (mat.emissive) {
                                        mat.emissive.set(0x000000);
                                    }
                                    // Behalte Original-Materialwerte
                                    mat.metalness = mat.userData.originalMetalness;
                                    mat.roughness = mat.userData.originalRoughness;
                                    mat.needsUpdate = true;
                                }
                            });

                            if (Array.isArray(mesh.material)) {
                                mesh.material = [...mesh.material];
                            }
                        });
                    }
                }
            }

            // Update side-color-row swatches for sides that inherit group color
            for (const [sideName, sideData] of groupData.sides.entries()) {
                if (!sideData.color) {
                    const sideColorRow = panelContainer?.querySelector(`.side-color-row[data-group="${groupName}"][data-side="${sideName}"]`);
                    const colorSwatchDisplay = sideColorRow?.querySelector('.color-swatch');

                    if (colorSwatchDisplay) {
                        colorSwatchDisplay.style.backgroundImage = 'url(/userdata/files/models/camo-texture.jpg)';
                        colorSwatchDisplay.style.backgroundSize = 'cover';
                        colorSwatchDisplay.style.backgroundColor = '';
                    }
                }
            }

            requestRender();
        });
    }

    function applySideCamoTexture(groupName, sideName) {
        getCamoTexture((camoTexture) => {
            const groups = window._configGroups;
            if (!groups || !groups.has(groupName)) {
                return;
            }
            const groupData = groups.get(groupName);
            if (!groupData || !groupData.sides) {
                return;
            }
            const sideData = groupData.sides.get(sideName);
            if (sideData) {
                sideData.color = 'camo';
            }

            sideMats.get(groupName)?.get(sideName)?.forEach(m => {
                if (m.userData.hasTextureOverride) return;

                // Speichere Original-Materialwerte beim ersten Mal
                if (m.userData.originalMetalness === undefined) {
                    m.userData.originalMetalness = m.metalness;
                    m.userData.originalRoughness = m.roughness;
                }

                m.map = camoTexture;
                m.color.set(0xffffff);
                if (m.emissive) {
                    m.emissive.set(0x000000);
                }
                // Behalte Original-Materialwerte
                m.metalness = m.userData.originalMetalness;
                m.roughness = m.userData.originalRoughness;
                m.needsUpdate = true;
            });

            const meshesForSide = sideMeshes.get(groupName)?.get(sideName);
            if (meshesForSide) {
                meshesForSide.forEach(mesh => {
                    getMaterialsArray(mesh.material).forEach(mat => {
                        if (mat && !mat.userData.hasTextureOverride) {
                            // Speichere Original-Materialwerte beim ersten Mal
                            if (mat.userData.originalMetalness === undefined) {
                                mat.userData.originalMetalness = mat.metalness;
                                mat.userData.originalRoughness = mat.roughness;
                            }

                            mat.map = camoTexture;
                            mat.color.set(0xffffff);
                            if (mat.emissive) {
                                mat.emissive.set(0x000000);
                            }
                            // Behalte Original-Materialwerte
                            mat.metalness = mat.userData.originalMetalness;
                            mat.roughness = mat.userData.originalRoughness;
                            mat.needsUpdate = true;
                        }
                    });
                });
            }

            requestRender();
        });
    }

    function resetGroupToOriginal(groupName) {
        const groups = window._configGroups;
        if (!groups || !groups.has(groupName)) {
            return;
        }
        const groupData = groups.get(groupName);
        if (!groupData || !groupData.sides) {
            return;
        }
        groupData.color = null;

        for (const [sideName, sideData] of groupData.sides.entries()) {
            if (!sideData.color) {
                const meshesForSide = sideMeshes.get(groupName)?.get(sideName);
                if (meshesForSide) {
                    meshesForSide.forEach(mesh => {
                        const origMaterials = originalMaterials.get(mesh.uuid);
                        if (origMaterials) {
                            mesh.material = Array.isArray(mesh.material)
                                ? origMaterials.map(m => m.clone())
                                : origMaterials[0].clone();
                        }
                    });

                    // Wichtig: Entferne initialized-Flag damit Decals neu angewendet werden
                    meshesForSide.forEach(mesh => {
                        state.initializedMeshes?.delete(mesh.uuid);
                    });
                }
            }
        }

        // Decals neu anwenden nach Reset
        updateAllMaterialsWithGraphicLayers();

        requestRender();
    }

    function applySideColor(groupName, sideName, color) {
        const groups = window._configGroups;
        if (!groups || !groups.has(groupName)) {
            return;
        }

        const groupData = groups.get(groupName);
        if (!groupData || !groupData.sides) {
            return;
        }
        const sideData = groupData.sides.get(sideName);
        if (!sideData) {
            return;
        }

        sideData.color = color;

        updateSideMaterials(groupName, sideName, (mat) => {
            mat.userData.hasTextureOverride = false;

            // Speichere Original-Materialwerte beim ersten Mal
            if (mat.userData.originalMetalness === undefined) {
                mat.userData.originalMetalness = mat.metalness;
                mat.userData.originalRoughness = mat.roughness;
            }

            mat.color.set(color);
            if (mat.emissive) {
                mat.emissive.set(0x000000);
            }
            // Behalte Original-Materialwerte
            mat.metalness = mat.userData.originalMetalness;
            mat.roughness = mat.userData.originalRoughness;
            mat.needsUpdate = true;
        }, {disposeMap: true});

        requestRender();
    }

    if (panelContainer && !panelContainer.__hasCfgSlideEvents) {
        panelContainer.addEventListener('cfgslide:opened', (e) => {
            const item = e.target.closest('.cfgSlideItem');
            const side = item?.dataset.side || null;
            const group = item?.dataset.group || null;

            if (!side) return;

            const key = group ? `${group}:${side}` : side;
            setActiveSide(key);

            // Update color swatch display for this side
            // The side-color-row is in the Color panel's sides-list, not in this item
            if (group) {
                const groups = window._configGroups;
                const groupData = groups?.get(group);
                const sideData = groupData?.sides?.get(side);

                // Find the side-color-row by data attributes
                const sideColorRow = panelContainer.querySelector(`.side-color-row[data-group="${group}"][data-side="${side}"]`);
                const colorSwatchDisplay = sideColorRow?.querySelector('.color-swatch');

                if (colorSwatchDisplay && groupData) {
                    const currentColor = sideData?.color || groupData.color;

                    if (currentColor === 'camo') {
                        colorSwatchDisplay.style.backgroundImage = 'url(/userdata/files/models/camo-texture.jpg)';
                        colorSwatchDisplay.style.backgroundSize = 'cover';
                        colorSwatchDisplay.style.backgroundColor = '';
                    } else if (currentColor) {
                        colorSwatchDisplay.style.background = currentColor;
                        colorSwatchDisplay.style.backgroundImage = '';
                    }
                }
            }

            focusCameraOnSide(side, group);
        });

        panelContainer.__hasCfgSlideEvents = true;
    }
    } catch (error) {
        console.error('[buildUIFromMarkers] Error building UI:', error);
        console.error('[buildUIFromMarkers] Stack:', error.stack);
        // Debug: Previously showed alert on iOS; replaced with console logging to avoid intrusive alerts
        if (window.DEBUG_IOS_ERRORS) {
            console.error('[buildUIFromMarkers] Error (iOS debug):', error);
        }
        // Don't re-throw to prevent page reload on iOS
    }
}

// ===== BUILD FEATURE TOGGLES (Geländer/Terrasse etc.) =====
/**
 * Baut die UI-Slide für schaltbare Ausstattungsteile (z.B. Geländer, Terrasse).
 * Gruppen kommen aus Node-Namen "configurator-<key>" im GLB (state.featureGroups),
 * nicht aus Materialnamen wie bei den Farbgruppen. Schalten setzt nur mesh.visible,
 * kein Modell-Reload, damit Decals/Grafiken erhalten bleiben.
 * @param {Map<string, Set<THREE.Object3D>>} featureGroups
 */
export function buildFeatureTogglesUI(featureGroups) {
    try {
        const GLOBALS = window.GLOBALS || { tc: {} };
        const panelContainer = document.querySelector('.configuratorPanel');
        if (!panelContainer || !featureGroups || featureGroups.size === 0) return;

        const featureOrder = ['railing', 'terrace'];
        const sortedKeys = Array.from(featureGroups.keys()).sort((a, b) => {
            const indexA = featureOrder.indexOf(a);
            const indexB = featureOrder.indexOf(b);
            if (indexA !== -1 && indexB !== -1) return indexA - indexB;
            if (indexA !== -1) return -1;
            if (indexB !== -1) return 1;
            return a.localeCompare(b);
        });

        const featureSlide = document.createElement('div');
        featureSlide.className = 'cfgSlideItem';
        featureSlide.dataset.type = 'feature-toggles';
        featureSlide.innerHTML = `
            <div class="cfgSlideItem__headline">${GLOBALS.tc.feature_toggles || 'Ausstattung'}</div>
            <div class="cfgSlideItem__content" style="display:none;">
                <div class="feature-toggles-list"></div>
            </div>
        `;

        const list = featureSlide.querySelector('.feature-toggles-list');

        sortedKeys.forEach(key => {
            const meshes = featureGroups.get(key);
            if (!meshes || meshes.size === 0) return;

            const inputId = `feature-toggle-${key}`;
            const row = document.createElement('label');
            row.className = 'specialcheckbox feature-toggle-row';
            row.setAttribute('for', inputId);
            row.innerHTML = `
                <input type="checkbox" id="${inputId}" checked>
                <i></i>
                <span>${getLabelText(key)}</span>
            `;

            const checkbox = row.querySelector('input');
            checkbox.addEventListener('change', () => {
                meshes.forEach(mesh => { mesh.visible = checkbox.checked; });
                rebuildOccludeTargets();
                // Schatten-Silhouette neu berechnen - sonst bleibt z.B. der Terrassen-Schatten
                // sichtbar, obwohl die Terrasse selbst gerade ausgeblendet wurde.
                if (state.modelRoot) createFakeShadow(state.modelRoot);
                updateColorRowsForFeatureVisibility();
                requestRender();
            });

            list.appendChild(row);
        });

        // An 2. Stelle einfügen - direkt nach der Modellgrößen-Auswahl (bzw. nach dem
        // Panel-Header, wenn keine Größenauswahl angezeigt wird). Siehe AB #230805.
        const sizeSlide = panelContainer.querySelector('#modelSizeSelector');
        const panelHeader = panelContainer.querySelector('.panelHeader');
        if (sizeSlide) {
            sizeSlide.insertAdjacentElement('afterend', featureSlide);
        } else if (panelHeader) {
            panelHeader.insertAdjacentElement('afterend', featureSlide);
        } else {
            panelContainer.prepend(featureSlide);
        }

        // Initialer Abgleich, falls beim Laden schon etwas ausgeblendet ist (z.B. aus einer
        // gespeicherten Konfiguration) - Farbauswahl für unsichtbare Teile nicht anzeigen.
        updateColorRowsForFeatureVisibility();
    } catch (error) {
        console.error('[buildFeatureTogglesUI] Error building UI:', error);
    }
}

/**
 * Blendet Farbauswahl-Zeilen (.group-color-row / .side-color-row) aus, wenn KEINES der
 * zugehörigen Meshes gerade sichtbar ist - z.B. weil das Feature (Geländer/Terrasse), zu
 * dem sie gehören, gerade weggeschaltet wurde. Verhindert, dass man eine Farbe für ein
 * Teil ändert, das man gerade gar nicht sieht.
 */
function updateColorRowsForFeatureVisibility() {
    const panelContainer = document.querySelector('.configuratorPanel');
    if (!panelContainer) return;

    // Sichtbarkeit kaskadiert in three.js über die Elternkette - ein Feature-Toggle setzt
    // .visible ggf. auf einer übergeordneten Gruppe, nicht auf dem Mesh selbst.
    function isEffectivelyVisible(obj) {
        let o = obj;
        while (o) {
            if (o.visible === false) return false;
            o = o.parent;
        }
        return true;
    }

    function meshesForRow(group, side) {
        if (side) {
            return state.sideMeshes.get(group)?.get(side) || null;
        }
        const sidesMap = state.sideMeshes.get(group);
        if (!sidesMap) return null;
        const merged = new Set();
        sidesMap.forEach(set => set.forEach(m => merged.add(m)));
        return merged;
    }

    function hasVisibleMesh(group, side) {
        const meshes = meshesForRow(group, side);
        if (!meshes || meshes.size === 0) return true; // keine bekannte Zuordnung -> nicht ausblenden
        for (const mesh of meshes) {
            if (isEffectivelyVisible(mesh)) return true;
        }
        return false;
    }

    panelContainer.querySelectorAll('.group-color-row[data-group], .side-color-row[data-group]').forEach(row => {
        const group = row.dataset.group;
        const side = row.dataset.side || null;
        row.style.display = hasVisibleMesh(group, side) ? '' : 'none';
    });

    panelContainer.querySelectorAll('.side-group-switch option[value]').forEach(opt => {
        if (opt.value === 'groups') return;
        const [group, side] = opt.value.split(':');
        if (!group || !side) return;
        opt.hidden = !hasVisibleMesh(group, side);
    });
}

// ===== SAFARI-SAFE COLOR PICKER OPENER =====
function openColorPickerSafely(inputEl, originEvent) {
    // iOS FIX: iOS requires that click() is called directly from the user gesture stack.
    // Using setTimeout breaks this chain and causes the first click to be ignored.

    // Detect iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    if (isIOS) {
        // iOS requires the input to be truly interactive from the start
        // Position it exactly over the button that was clicked
        const button = originEvent?.target?.closest('.nativeColorBtn');
        if (button) {
            const rect = button.getBoundingClientRect();
            const parent = inputEl.parentElement;
            const parentRect = parent.getBoundingClientRect();

            // Position input exactly over the button
            inputEl.style.position = 'absolute';
            inputEl.style.left = (rect.left - parentRect.left) + 'px';
            inputEl.style.top = (rect.top - parentRect.top) + 'px';
            inputEl.style.width = rect.width + 'px';
            inputEl.style.height = rect.height + 'px';
            inputEl.style.opacity = '0.01'; // Nearly invisible but interactive
            inputEl.style.pointerEvents = 'auto';
            inputEl.style.zIndex = '1000';

            // iOS: Call click() SYNCHRONOUSLY to preserve user gesture chain
            // Do NOT use setTimeout here - it breaks iOS user gesture detection
            inputEl.focus();
            inputEl.click();

            return;
        }
    }

    // Non-iOS or fallback: Use original approach
    const originalStyle = {
        position: inputEl.style.position,
        opacity: inputEl.style.opacity,
        pointerEvents: inputEl.style.pointerEvents
    };

    inputEl.style.position = 'absolute';
    inputEl.style.opacity = '0.01'; // Nearly invisible but still interactive
    inputEl.style.pointerEvents = 'auto';

    // Try showPicker API first (works in Safari 16.4+ and modern browsers)
    if (typeof inputEl.showPicker === 'function') {
        try {
            inputEl.showPicker();
            // Restore styles after a delay
            setTimeout(() => {
                inputEl.style.position = originalStyle.position;
                inputEl.style.opacity = originalStyle.opacity;
                inputEl.style.pointerEvents = originalStyle.pointerEvents;
            }, 100);
            return;
        } catch (e) {
            // showPicker can throw if not called from user gesture
            // fall through to click fallback
        }
    }

    // Fallback: direct click (works on most browsers including iOS)
    try {
        inputEl.click();
        // Restore styles after a delay
        setTimeout(() => {
            inputEl.style.position = originalStyle.position;
            inputEl.style.opacity = originalStyle.opacity;
            inputEl.style.pointerEvents = originalStyle.pointerEvents;
        }, 100);
    } catch (e) {
        console.warn('Color picker could not be opened:', e);
        // Restore styles even on error
        inputEl.style.position = originalStyle.position;
        inputEl.style.opacity = originalStyle.opacity;
        inputEl.style.pointerEvents = originalStyle.pointerEvents;
    }
}
